import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from 'react-modal';
import { createPortal } from 'react-dom';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  MoreVertical,
  Info,
  X,
  Link,
  CalendarDays,
  MapPin,
  ChevronLeft,
  ChevronDown,
  List as ListIcon,
  Plus,
  Pen,
  Trash2,
  SortDesc
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import './index.css';
import {
  createBackupPayload,
  normalizeLocationOrderBackupState,
  normalizeProjectBackupState,
  normalizeRestoredLists,
  normalizeSortModeBackupState
} from './backupUtils';

Modal.setAppElement('#root');

const CROSS_OUT_DURATION = 0.25;
const RECENTLY_DELETED_ID = '__RECENTLY_DELETED__';
const RECENT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const isNativeApp = () => {
  if (typeof window === "undefined" || !window.Capacitor) return false;
  if (typeof window.Capacitor.isNativePlatform === "function") {
    return window.Capacitor.isNativePlatform();
  }
  return !!window.Capacitor.isNative;
};

const staticPageStyle = () => (
  isNativeApp() ? { paddingTop: "calc(env(safe-area-inset-top) + 2.5rem)" } : undefined
);

function BackButton() {
  const goBack = (event) => {
    event.preventDefault();
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign("/");
  };

  return (
    <a
      href="/"
      onClick={goBack}
      className="inline-flex items-center gap-1.5 mb-10 bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] pl-4 pr-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
    >
      <ChevronLeft size={18} strokeWidth={2.5} />
      Back
    </a>
  );
}

function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy - Mini List";
  }, []);

  return (
    <main
      className="min-h-screen bg-[color:var(--app-bg)] text-[color:var(--app-text)] px-5 py-10 sm:px-8"
      style={staticPageStyle()}
    >
      <div className="mx-auto max-w-2xl">
        <BackButton />

        <h1 className="text-4xl font-bold mb-3">Privacy Policy</h1>
        <p className="text-[color:var(--app-text-muted)] mb-10">Last updated: May 14, 2026</p>

        <div className="space-y-8 text-base leading-7 text-[color:var(--app-text-secondary)]">
          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Overview</h2>
            <p>
              Mini List is a simple to-do list app designed to store your lists and tasks locally on your device. We do not require an account, and we do not sell your personal information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Information We Collect</h2>
            <p>
              Mini List does not currently collect personal information from you. Your lists, task names, task details, locations, links, and deleted items are stored locally on your device using local app/browser storage.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Local Data Storage</h2>
            <p>
              The information you enter into Mini List remains on your device unless you choose to export, share, back up, restore, or otherwise transfer it yourself. If you delete the app, clear browser data, reset your device, or remove app storage, your locally stored Mini List data may be deleted.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Backups and Imports</h2>
            <p>
              Mini List may allow you to create a backup file or restore from a backup file. Backup files are created and handled by you. If you save a backup to iCloud, email, another app, or another storage provider, that provider's privacy practices apply to the copy you store there.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Links and Third-Party Websites</h2>
            <p>
              You may add website links to tasks. Opening those links may take you to third-party websites or apps. Mini List is not responsible for the privacy practices of those third parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Analytics, Tracking, and Advertising</h2>
            <p>
              Mini List does not currently use third-party analytics, advertising SDKs, cross-app tracking, or tracking technologies. If we add analytics, advertising, account features, cloud sync, or other data collection in the future, we will update this Privacy Policy and any applicable App Store privacy disclosures.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Children's Privacy</h2>
            <p>
              Mini List is not directed to children under 13, and we do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Security</h2>
            <p>
              Because Mini List stores task data locally, protecting your device, device passcode, Apple ID, backups, and browser/app storage helps protect your Mini List data. No method of storage or transmission is completely secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we will update the date at the top of this page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-3">Contact</h2>
            <p>
              If you have questions about this Privacy Policy, contact us at <a className="text-[color:var(--app-text)] underline underline-offset-4" href="mailto:support@mini-list.com">support@mini-list.com</a>.
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}

function AboutPage() {
  useEffect(() => {
    document.title = "About Mini List";
  }, []);

  return (
    <main
      className="min-h-screen bg-[color:var(--app-bg)] text-[color:var(--app-text)] px-5 py-10 sm:px-8"
      style={staticPageStyle()}
    >
      <div className="mx-auto max-w-2xl">
        <BackButton />

        <h1 className="text-4xl font-bold mb-4">About Mini List</h1>
        <p className="text-lg leading-8 text-[color:var(--app-text-secondary)] mb-8">
          Mini List is a simple to-do list app focused on fast list creation, local storage, and a minimal interface.
        </p>

        <div className="space-y-4 text-base leading-7 text-[color:var(--app-text-secondary)]">
          <p>
            Your lists and tasks are currently stored locally on your device. Mini List does not require an account and does not currently use third-party tracking or advertising.
          </p>
          <p>
            Read the <a className="text-[color:var(--app-text)] underline underline-offset-4" href="/privacy">Privacy Policy</a>.
          </p>
          <p>
            Contact: <a className="text-[color:var(--app-text)] underline underline-offset-4" href="mailto:support@mini-list.com">support@mini-list.com</a>
          </p>
        </div>
      </div>
    </main>
  );
}

function App() {
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") : "";

  if (path === "/privacy") {
    return <PrivacyPolicy />;
  }

  if (path === "/about") {
    return <AboutPage />;
  }

  return <TodoApp />;
}

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

function TodoApp() {
  // App-level state
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [listsHydrated, setListsHydrated] = useState(false);
  const [suppressFirstAnim, setSuppressFirstAnim] = useState(true);
  const [restoreDone, setRestoreDone] = useState(false);

  // ---- Locations directory (global across all lists) ----
  // Each location: { nickname: string, address: string }
  const [locations, setLocations] = useState([]);
  // Details modal location dropdown state/refs
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const locationButtonRef = useRef(null);
  const locationMenuRef = useRef(null);
  const [locationDropdownRect, setLocationDropdownRect] = useState(null);

  // Sort mode per-list and section collapse state
  const NO_LOCATION_KEY = '__NONE__';
  // Normalize location section keys so "No location" and blanks map to NO_LOCATION_KEY
  const normalizeLocKey = (k) => {
    const s = (k ?? '').trim();
    if (!s || s === 'No location' || s === NO_LOCATION_KEY) return NO_LOCATION_KEY;
    return s;
  };
  const [sortModeByList, setSortModeByList] = useState({}); // { [listId]: 'priority' | 'project' | 'location' }
  const [collapsedSections, setCollapsedSections] = useState({}); // { [sectionKey]: boolean }
  const [collapsedProjectsByList, setCollapsedProjectsByList] = useState({}); // { [listId]: { [taskId]: boolean } }
  const collapsedProjects = selectedListId ? (collapsedProjectsByList[selectedListId] || {}) : {};
  const updateCollapsedProjects = (updater) => {
    if (!selectedListId || selectedListId === RECENTLY_DELETED_ID) return;
    setCollapsedProjectsByList(prev => {
      const current = prev[selectedListId] || {};
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [selectedListId]: next && typeof next === 'object' ? next : {} };
    });
  };

  // Independent ordering for Location view: { [listId]: { [locationKey]: string[] /* ordered task IDs */ } }
  const [locationOrderByList, setLocationOrderByList] = useState({});
  const [projectTopOrderByList, setProjectTopOrderByList] = useState({}); // { [listId]: string[] }
  const [projectChildOrderByList, setProjectChildOrderByList] = useState({}); // { [listId]: { [parentTaskId]: string[] } }

  // App notices (replaces alert())
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const openNotice = (title, message) => { setNoticeTitle(title); setNoticeMessage(message); setNoticeOpen(true); };
  const closeNotice = () => setNoticeOpen(false);

  // List overview state
  const [newListName, setNewListName] = useState("");
  const [showAddListModal, setShowAddListModal] = useState(false);
    // Add List modal: user's chosen initial sort for the new list
  const [addListSortChoice, setAddListSortChoice] = useState('priority'); // 'priority' | 'project' | 'location'
  // Unified sticky header menu
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [sortMenuExpanded, setSortMenuExpanded] = useState(false);
  const headerMenuRef = useRef(null);
  const headerMenuButtonRef = useRef(null);

  // Uniform card height across all rows on the Lists page
  const [uniformCardHeight, setUniformCardHeight] = useState(null);
  // Ref to the Lists overview grid container for measuring cards
  const overviewGridRef = useRef(null);
  // Measure tallest list card and enforce uniform height across all tiles (reactive, immediate)
  useEffect(() => {
    if (selectedListId !== null) return; // only on overview

    const container = overviewGridRef.current || document;

    const measure = () => {
      const nodes = Array.from(container.querySelectorAll('[data-list-card="true"]'));
      if (!nodes.length) { setUniformCardHeight(null); return; }
      let max = 0;
      nodes.forEach(el => {
        // Temporarily clear inline sizing to capture natural height
        const prevH = el.style.height;
        const prevMinH = el.style.minHeight;
        el.style.height = '';
        el.style.minHeight = '';
        // Force layout and measure natural height
        const h = el.getBoundingClientRect().height;
        if (h > max) max = h;
        // Restore previous inline styles
        el.style.height = prevH;
        el.style.minHeight = prevMinH;
      });
      setUniformCardHeight(max || null);
    };

    const scheduleAfterPaint = () => {
      // double-rAF + microtask ensures layout & line wrapping have settled
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(measure, 0);
        });
      });
    };

    // Initial schedule
    scheduleAfterPaint();

    // Observe size changes of individual cards
    const nodes = Array.from(container.querySelectorAll('[data-list-card="true"]'));
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => scheduleAfterPaint()) : null;
    nodes.forEach(n => ro && ro.observe(n));

    // Observe DOM mutations (new cards, text changes)
    const mo = new MutationObserver(() => scheduleAfterPaint());
    mo.observe(container, { childList: true, subtree: true, characterData: true });

    // Window resizes
    const onResize = () => scheduleAfterPaint();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      mo.disconnect();
    };
  }, [lists, selectedListId]);

  // Single list state
  const [tasks, setTasks] = useState([]);
  const [input, setInput] = useState("");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [openDialogId, setOpenDialogId] = useState(null);
  const [animatingTask, setAnimatingTask] = useState(null);
  const [title, setTitle] = useState("To Do");
  const [editingTitle, setEditingTitle] = useState(false);

  // Delete list modal
  const [showDeleteListModal, setShowDeleteListModal] = useState(false);
  // Recently Deleted state
  const [recentlyDeleted, setRecentlyDeleted] = useState([]); // [{ id, task, fromListId, deletedAt }]
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const [recoverItemId, setRecoverItemId] = useState(null);
  const [showDeleteAllDeletedModal, setShowDeleteAllDeletedModal] = useState(false);
  // Add Location sheet state
  const [showAddLocationSheet, setShowAddLocationSheet] = useState(false);
  const [newLocNick, setNewLocNick] = useState("");
  const [newLocAddr, setNewLocAddr] = useState("");
  const [newLocError, setNewLocError] = useState("");

  // Manage/Edit Locations sheet state
  const [showManageLocationsSheet, setShowManageLocationsSheet] = useState(false);
  const [showEditLocationSheet, setShowEditLocationSheet] = useState(false);
  const [editLocNickOriginal, setEditLocNickOriginal] = useState("");
  const [editLocNick, setEditLocNick] = useState("");
  const [editLocAddr, setEditLocAddr] = useState("");
  const [editLocError, setEditLocError] = useState("");
  const [confirmingDeleteLoc, setConfirmingDeleteLoc] = useState(false);
  const [deleteReassignTo, setDeleteReassignTo] = useState("__NONE__"); // nickname or NO_LOCATION_KEY

  // Edit Location: reassign dropdown state/refs (confirmation panel)
  const [reassignDropdownOpen, setReassignDropdownOpen] = useState(false);
  const reassignButtonRef = useRef(null);
  const reassignMenuRef = useRef(null);
  const [reassignDropdownRect, setReassignDropdownRect] = useState(null);

  // Track which task is currently being edited (bug fix)
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [revealedInfoTaskId, setRevealedInfoTaskId] = useState(null);
  const revealedInfoTaskRef = useRef(null);
  const taskSwipeRef = useRef(null);
  const editDelayRef = useRef(null);
  const editOriginalTextRef = useRef(null);
  const dragScrollLockRef = useRef(null);
  const overviewScrollRef = useRef(null);
  const recentlyDeletedScrollRef = useRef(null);
  const listScrollRef = useRef(null);
  const scrollResetRafRef = useRef(null);
  const dragPointerXRef = useRef(0);
  const dragPointerYRef = useRef(0);
  const dragPointerOffsetXRef = useRef(0);
  const dragPointerOffsetYRef = useRef(0);
  const dragCardHeightRef = useRef(0);
  const isDraggingRef = useRef(false);
  const dragAutoScrollRafRef = useRef(null);
  const [canHover, setCanHover] = useState(false);
  // Temp selected list for currently open Details modal
  const [selectedListForDialog, setSelectedListForDialog] = useState(null);
  const [taskDetailsView, setTaskDetailsView] = useState('details');
  const [subtaskInput, setSubtaskInput] = useState('');
  // Dropdown open state for the custom List select in the Details modal
  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  // Ref and positioning for portal dropdown
  const listButtonRef = useRef(null);
  const listMenuRef = useRef(null);
  const [listDropdownRect, setListDropdownRect] = useState(null);
  const dueDateInputRef = useRef(null);
  const [dueDateCalendarOpen, setDueDateCalendarOpen] = useState(false);
  const [dueDateCalendarMonth, setDueDateCalendarMonth] = useState(null);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const projectButtonRef = useRef(null);
  const projectMenuRef = useRef(null);
  const [projectDropdownRect, setProjectDropdownRect] = useState(null);
  const [projectDropTargetId, setProjectDropTargetId] = useState(null);
  const projectDropTargetRef = useRef(null);
  const projectDraggingTaskIdRef = useRef(null);
  // Keep last-opened task around so the shared modal can animate out with its content
  const lastDialogTaskRef = useRef(null);
  // Backup import <input type="file"> ref
  const importInputRef = useRef(null);
  // Textarea ref for autosizing the task editor
  const editingTextareaRef = useRef(null);
  // Preserve original list title when entering edit mode
  const originalTitleRef = useRef("");
  const restoredLastViewRef = useRef(false);
  // Prevent save-effect from running while we hydrate view state from a list (avoids ping-pong)
  const hydratingFromListRef = useRef(false);
  // Multi-line strikethrough support
  const textRefs = useRef({}); // map taskId -> span element
  const [strikeRectsByTask, setStrikeRectsByTask] = useState({}); // taskId -> [{left, top, width, height}]

  // Track link pill elements and whether each has wrapped to a new line
  const linkRefs = useRef({}); // taskId -> anchor element
  const [linkWrappedByTask, setLinkWrappedByTask] = useState({}); // taskId -> boolean

  // Retrieve last viewed location from localStorage with legacy key fallback
  const getSavedLastView = () => {
    try {
      // Preferred
      let saved = localStorage.getItem('last_view_v1');
      if (saved) return saved;
      // Legacy fallbacks
      const legacyKeys = ['last_view', 'lastViewed', 'lastViewedListId', 'lastListId'];
      for (const k of legacyKeys) {
        const v = localStorage.getItem(k);
        if (v) return v;
      }
    } catch {}
    return null;
  };

  const beginEditingTitle = () => {
    originalTitleRef.current = title;
    setEditingTitle(true);
  };

  const finalizeTitleEditing = () => {
    const trimmed = (title ?? "").trim();
    if (trimmed === "") {
      // Restore original non-empty title if user tries to save empty
      setTitle(originalTitleRef.current || "To Do");
    }
    setEditingTitle(false);
  };

  // --- Storage ---
  useEffect(() => {
    try {
      const storedLists = localStorage.getItem("lists_v2");
      if (storedLists) setLists(JSON.parse(storedLists));
    } catch {}
    // Mark hydration complete even if nothing was stored
    setListsHydrated(true);
  }, []);

  // Hydrate global locations directory
  useEffect(() => {
    try {
      const raw = localStorage.getItem('locations_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setLocations(parsed.filter(l => l && typeof l.nickname === 'string'));
      }
    } catch {}
  }, []);

  // Hydrate per-list sort mode
useEffect(() => {
  try {
    const raw = localStorage.getItem('sort_mode_by_list_v1');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') setSortModeByList(parsed);
    }
  } catch {}
}, []);

  // Hydrate per-list per-location order (independent of Priority order)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('location_order_by_list_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setLocationOrderByList(parsed);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const rawTop = localStorage.getItem('project_top_order_by_list_v1');
      if (rawTop) {
        const parsed = JSON.parse(rawTop);
        if (parsed && typeof parsed === 'object') setProjectTopOrderByList(parsed);
      }
      const rawChildren = localStorage.getItem('project_child_order_by_list_v1');
      if (rawChildren) {
        const parsed = JSON.parse(rawChildren);
        if (parsed && typeof parsed === 'object') setProjectChildOrderByList(parsed);
      }
      const rawCollapsed = localStorage.getItem('project_collapsed_by_list_v1');
      if (rawCollapsed) {
        const parsed = JSON.parse(rawCollapsed);
        if (parsed && typeof parsed === 'object') setCollapsedProjectsByList(parsed);
      }
    } catch {}
  }, []);

  // Hydrate Recently Deleted from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('recently_deleted_v1');
      if (stored) {
        const parsed = JSON.parse(stored) || [];
        setRecentlyDeleted(Array.isArray(parsed) ? parsed : []);
      }
    } catch {}
  }, []);

  // Persist Recently Deleted and prune items older than 30 days
  useEffect(() => {
    const now = Date.now();
    const pruned = (recentlyDeleted || []).filter(it => {
      const t = typeof it?.deletedAt === 'number' ? it.deletedAt : 0;
      return now - t <= RECENT_TTL_MS;
    });
    if (pruned.length !== recentlyDeleted.length) {
      setRecentlyDeleted(pruned);
      try { localStorage.setItem('recently_deleted_v1', JSON.stringify(pruned)); } catch {}
    } else {
      try { localStorage.setItem('recently_deleted_v1', JSON.stringify(recentlyDeleted)); } catch {}
    }
  }, [recentlyDeleted]);

  useEffect(() => {
    localStorage.setItem("lists_v2", JSON.stringify(lists));
  }, [lists]);

  // Persist global locations directory
  useEffect(() => {
    try { localStorage.setItem('locations_v1', JSON.stringify(locations)); } catch {}
  }, [locations]);

  // Persist per-list sort mode
  useEffect(() => {
    try { localStorage.setItem('sort_mode_by_list_v1', JSON.stringify(sortModeByList)); } catch {}
  }, [sortModeByList]);

  // Persist per-list per-location order
useEffect(() => {
  try {
    localStorage.setItem('location_order_by_list_v1', JSON.stringify(locationOrderByList));
  } catch {}
}, [locationOrderByList]);

  useEffect(() => {
    try { localStorage.setItem('project_top_order_by_list_v1', JSON.stringify(projectTopOrderByList)); } catch {}
  }, [projectTopOrderByList]);

  useEffect(() => {
    try { localStorage.setItem('project_child_order_by_list_v1', JSON.stringify(projectChildOrderByList)); } catch {}
  }, [projectChildOrderByList]);

  useEffect(() => {
    try { localStorage.setItem('project_collapsed_by_list_v1', JSON.stringify(collapsedProjectsByList)); } catch {}
  }, [collapsedProjectsByList]);

  // Load single list's tasks when selectedListId or lists change (guarded + hydration flag)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (selectedListId == null) return;
    if (selectedListId === RECENTLY_DELETED_ID) return;

    const found = lists.find(l => l.id === selectedListId);

    const nextTitle = typeof found?.title !== 'undefined' ? found.title : "To Do";
    const nextTasks = found?.tasks || [];
    const nextHideCompleted = Boolean(found?.hideCompleted);

    const willChange = (title !== nextTitle) || (tasks !== nextTasks) || (hideCompleted !== nextHideCompleted);
    if (!willChange) return; // nothing to do

    // Mark that we're hydrating view state from list data; the save-effect must ignore this pass
    hydratingFromListRef.current = true;

    if (title !== nextTitle) setTitle(nextTitle);
    if (tasks !== nextTasks) setTasks(nextTasks);
    if (hideCompleted !== nextHideCompleted) setHideCompleted(nextHideCompleted);

    // Reset the hydration flag on the next microtask after React has committed these setStates
    Promise.resolve().then(() => { hydratingFromListRef.current = false; });
  }, [selectedListId, lists]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Restore last visited view on load (once), after lists are available
  useEffect(() => {
    if (!listsHydrated) return; // wait until lists are loaded from storage
    if (restoredLastViewRef.current) return;
    try {
      const saved = getSavedLastView();
      let targetId = null;
      if (saved && saved !== 'overview') {
        // 1) Exact id match
        if (lists.some(l => l.id === saved)) {
          targetId = saved;
        } else {
          // 2) Numeric index fallback from very old versions
          const idx = Number.isNaN(Number(saved)) ? null : Number(saved);
          if (idx !== null && idx >= 0 && idx < lists.length) {
            targetId = lists[idx].id;
          } else {
            // 3) Title match fallback (case-insensitive)
            const byTitle = lists.find(l => (l.title || '').toLowerCase() === (saved || '').toLowerCase());
            if (byTitle) targetId = byTitle.id;
          }
        }
      }

      if (targetId) {
        setSelectedListId(targetId);
        // migrate to new key
        try { localStorage.setItem('last_view_v1', targetId); } catch {}
      } else if (saved === 'overview') {
        setSelectedListId(null);
        try { localStorage.setItem('last_view_v1', 'overview'); } catch {}
      }
    } catch {}
    restoredLastViewRef.current = true;
    setRestoreDone(true);
  }, [listsHydrated, lists]);

  // Disable slide-in animation only after the first restored view has painted
  useEffect(() => {
    if (!restoreDone || !suppressFirstAnim) return;
    let raf = requestAnimationFrame(() => {
      setSuppressFirstAnim(false);
    });
    return () => cancelAnimationFrame(raf);
  }, [restoreDone, suppressFirstAnim]);

  // Handle outside clicks for header menu
  useEffect(() => {
    if (!headerMenuOpen) return;
    const onPointerDown = (e) => {
      if (headerMenuRef.current && headerMenuRef.current.contains(e.target)) return;
      if (headerMenuButtonRef.current && headerMenuButtonRef.current.contains(e.target)) return;
      setHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [headerMenuOpen]);

  useEffect(() => {
    const onPointerDown = (e) => {
      const target = e.target;
      if (!revealedInfoTaskRef.current) return;
      if (target && target.closest('[data-task-info-button="true"]')) return;
      setRevealedInfoTaskId(null);
    };

    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, []);

  useEffect(() => {
    if (editingTaskId != null) {
      setRevealedInfoTaskId(null);
    }
  }, [editingTaskId]);

  // Save task changes to the lists array (only when something actually changed)
  useEffect(() => {
    if (selectedListId == null) return;
    if (selectedListId === RECENTLY_DELETED_ID) return; // never persist from the pseudo view
    if (hydratingFromListRef.current) return; // ignore updates triggered by the load-effect

    setLists(prev => {
      const idx = prev.findIndex(l => l.id === selectedListId);
      if (idx === -1) return prev;

      const curr = prev[idx];
      const sameTitle = curr.title === title;
      const sameHide = Boolean(curr.hideCompleted) === Boolean(hideCompleted);
      const sameTasksRef = curr.tasks === tasks; // reference equality is sufficient here

      if (sameTitle && sameHide && sameTasksRef) {
        // Nothing actually changed; return previous state to avoid creating new arrays
        return prev;
      }

      const next = [...prev];
      next[idx] = { ...curr, tasks, title, hideCompleted };
      return next;
    });
    // eslint-disable-next-line
  }, [tasks, title, hideCompleted, selectedListId]);

  // Persist last visited view (overview or a specific list)
  useEffect(() => {
    // Don't write until after we've attempted a restore
    if (!restoredLastViewRef.current) return;
    const value = selectedListId ?? 'overview';
    try {
      localStorage.setItem('last_view_v1', value);
      // Clean legacy keys to prevent future confusion
      ['last_view', 'lastViewed', 'lastViewedListId', 'lastListId'].forEach(k => localStorage.removeItem(k));
    } catch {}
  }, [selectedListId]);

  useEffect(() => {
    setRevealedInfoTaskId(null);
  }, [selectedListId]);

  const resetIncomingScrollNode = useCallback((node) => {
    if (!node) return;
    node.scrollTop = 0;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch {}
    if (scrollResetRafRef.current) cancelAnimationFrame(scrollResetRafRef.current);
    scrollResetRafRef.current = requestAnimationFrame(() => {
      if (node.isConnected) node.scrollTop = 0;
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch {}
      scrollResetRafRef.current = null;
    });
  }, []);

  const setOverviewScrollNode = useCallback((node) => {
    overviewScrollRef.current = node;
    if (node && selectedListId == null) resetIncomingScrollNode(node);
  }, [resetIncomingScrollNode, selectedListId]);

  const setRecentlyDeletedScrollNode = useCallback((node) => {
    recentlyDeletedScrollRef.current = node;
    if (node && selectedListId === RECENTLY_DELETED_ID) resetIncomingScrollNode(node);
  }, [resetIncomingScrollNode, selectedListId]);

  const setListScrollNode = useCallback((node) => {
    listScrollRef.current = node;
    if (node && selectedListId != null && selectedListId !== RECENTLY_DELETED_ID) {
      resetIncomingScrollNode(node);
    }
  }, [resetIncomingScrollNode, selectedListId]);

  const resetActiveViewScroll = useCallback(() => {
    const activeScrollEl = selectedListId == null
      ? overviewScrollRef.current
      : selectedListId === RECENTLY_DELETED_ID
        ? recentlyDeletedScrollRef.current
        : listScrollRef.current;
    resetIncomingScrollNode(activeScrollEl);
  }, [resetIncomingScrollNode, selectedListId]);

  useEffect(() => {
    return () => {
      if (scrollResetRafRef.current) cancelAnimationFrame(scrollResetRafRef.current);
    };
  }, []);

  useEffect(() => {
    revealedInfoTaskRef.current = revealedInfoTaskId;
  }, [revealedInfoTaskId]);

  useEffect(() => {
    const media = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
    } else {
      media.addListener(update);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    if (canHover) {
      setRevealedInfoTaskId(null);
    }
  }, [canHover]);

  useEffect(() => {
    setDueDateCalendarOpen(false);
  }, [openDialogId]);

  useEffect(() => {
    return () => {
      if (editDelayRef.current) {
        clearTimeout(editDelayRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setTaskDetailsView('details');
    setSubtaskInput('');
  }, [openDialogId]);

  // --------- Backup / Restore (localStorage JSON) ---------
  const exportBackup = async () => {
    // Build a local-time filename: backup-YYYY-MM-DD-HH-MM-SS.json
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const filename = `backup-${yyyy}-${mm}-${dd}-${hh}-${min}-${ss}.json`;
    try {
      const payload = createBackupPayload({
        lists,
        locations,
        sortModeByList,
        locationOrderByList,
        projectTopOrderByList,
        projectChildOrderByList,
        projectCollapsedByList: collapsedProjectsByList,
        lastView: localStorage.getItem('last_view_v1') ?? 'overview',
        recentlyDeleted
      });
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });

      // Try native share sheet first (iOS/Android)
      const supportsFileShare = typeof navigator !== 'undefined' && 'share' in navigator && 'canShare' in navigator;
      if (supportsFileShare) {
        try {
          const file = new File([blob], filename, { type: 'application/json' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file]
            });
            openNotice('Backup shared', 'Your backup was saved.');
            return;
          }
        } catch (err) {
          // fall through to download
          console.warn('Share failed or unsupported, falling back to download', err);
        }
      }

      // Fallback: force a file download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename; // includes local time HH-MM-SS
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 0);
      openNotice('Backup created', 'Your backup has been saved.');
    } catch (e) {
      console.error(e);
      openNotice('Backup failed', 'We could not create your backup. Please try again.');
    }
  };

  const triggerImportBackup = () => {
    if (importInputRef.current) importInputRef.current.click();
  };

  const handleImportFile = async (evt) => {
    const file = evt.target.files && evt.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      if (!json || json._type !== 'minimalist-backup') throw new Error('Not a Minimalist backup file.');
      if (!json.data || !Array.isArray(json.data.lists)) throw new Error('Backup contents invalid.');
      // Basic normalization of list/task shapes. Task details are intentionally
      // preserved so fields like dueDate, link, locationNickname, and
      // projectTaskId survive backup round trips.
      const restoredLists = normalizeRestoredLists(json.data.lists || []);

      // Restore locations (gracefully handle older backups without locations)
      const incomingLocs = Array.isArray(json.data.locations) ? json.data.locations : [];
      const uniqueByNick = [];
      const seen = new Set();
      incomingLocs.forEach(l => {
        if (!l || typeof l.nickname !== 'string') return;
        const key = l.nickname.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        uniqueByNick.push({ nickname: l.nickname, address: typeof l.address === 'string' ? l.address : '' });
      });
      setLocations(uniqueByNick);
      try { localStorage.setItem('locations_v1', JSON.stringify(uniqueByNick)); } catch {}
    
      // Restore per-list sort mode. Older backups without this data default to Priority.
      const restoredSortModeByList = normalizeSortModeBackupState(restoredLists, json.data.sortModeByList);
      setSortModeByList(restoredSortModeByList);
      try { localStorage.setItem('sort_mode_by_list_v1', JSON.stringify(restoredSortModeByList)); } catch {}

      const restoredLocationOrderByList = normalizeLocationOrderBackupState(restoredLists, json.data.locationOrderByList);
      setLocationOrderByList(restoredLocationOrderByList);
      try { localStorage.setItem('location_order_by_list_v1', JSON.stringify(restoredLocationOrderByList)); } catch {}

      const {
        topByList: restoredProjectTopOrderByList,
        childrenByList: restoredProjectChildOrderByList,
        collapsedByList: restoredProjectCollapsedByList
      } = normalizeProjectBackupState(
        restoredLists,
        json.data.projectTopOrderByList,
        json.data.projectChildOrderByList,
        json.data.projectCollapsedByList
      );
      setProjectTopOrderByList(restoredProjectTopOrderByList);
      setProjectChildOrderByList(restoredProjectChildOrderByList);
      setCollapsedProjectsByList(restoredProjectCollapsedByList);
      try { localStorage.setItem('project_top_order_by_list_v1', JSON.stringify(restoredProjectTopOrderByList)); } catch {}
      try { localStorage.setItem('project_child_order_by_list_v1', JSON.stringify(restoredProjectChildOrderByList)); } catch {}
      try { localStorage.setItem('project_collapsed_by_list_v1', JSON.stringify(restoredProjectCollapsedByList)); } catch {}

      setLists(restoredLists);
      localStorage.setItem('lists_v2', JSON.stringify(restoredLists));

      // Normalize and restore Recently Deleted (optional for older backups)
      const rawDeleted = Array.isArray(json.data.recentlyDeleted) ? json.data.recentlyDeleted : [];
      const restoredDeleted = rawDeleted.map(e => {
        const task = e && e.task ? e.task : {};
        const normTask = {
          id: task.id || crypto.randomUUID(),
          text: typeof task.text === 'string' ? task.text : '',
          done: Boolean(task.done),
          details: task.details && typeof task.details === 'object' ? task.details : {}
        };
        const deletedAt = typeof e.deletedAt === 'number' ? e.deletedAt : Date.now();
        return {
          id: e.id || crypto.randomUUID(),
          task: normTask,
          fromListId: e.fromListId || null,
          deletedAt
        };
      }).filter(e => (Date.now() - (e.deletedAt || 0)) <= RECENT_TTL_MS);

      setRecentlyDeleted(restoredDeleted);
      try { localStorage.setItem('recently_deleted_v1', JSON.stringify(restoredDeleted)); } catch {}

      const last = json.data.lastView === 'overview' ? 'overview' : restoredLists.find(l => l.id === json.data.lastView)?.id || 'overview';
      localStorage.setItem('last_view_v1', last);
      setSelectedListId(last === 'overview' ? null : last);
      setHeaderMenuOpen(false);
      openNotice('Import complete', 'Your lists have been restored from your backup.');
    } catch (e) {
      console.error(e);
      openNotice('Import failed', 'That file could not be imported. Please select a valid Minimal list backup.');
    } finally {
      // reset the input so selecting the same file again still triggers change
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  // --------- List overview handlers ---------
const handleCreateList = () => {
  const name = newListName.trim() || `List ${lists.length + 1}`;
  const newId = crypto.randomUUID();
  
  setLists(prev => [
    ...prev,
    { id: newId, title: name, tasks: [], hideCompleted: false }
  ]);

  // persist the chosen initial sort for this new list
  setSortModeByList(prev => ({ ...prev, [newId]: addListSortChoice }));
  
  //Reset modal state
  setNewListName("");
  setAddListSortChoice('priority');
  setShowAddListModal(false);

  // optional: navigate into the new list
  setSelectedListId(newId); 
};

  // Delete list handler (now only accessible on single list page)
  const handleDeleteList = id => {
    const listToDelete = lists.find(l => l.id === id);
    if (listToDelete && Array.isArray(listToDelete.tasks)) {
      listToDelete.tasks.forEach(t => recordRecentlyDeleted(t, id));
    }
    setLists(prev => prev.filter(l => l.id !== id));
    setSelectedListId(null);
    setShowDeleteListModal(false);
  };


  // Sortable List Card (for dnd-kit)
  const SortableListCard = ({ list, onOpen }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: list.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 10 : 'auto',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none'
    };
    const activeTasks = (list.tasks || []).filter(task => !task.done);
    return (
      <div
        ref={setNodeRef}
        style={{ ...style, minHeight: uniformCardHeight ? uniformCardHeight : undefined }}
        {...attributes}
        {...listeners}
        data-list-card="true"
        className={`relative group bg-[color:var(--app-card-bg)] rounded-2xl p-6 min-h-[96px] cursor-pointer select-none transition-colors shadow-sm hover:shadow-md dark:hover:shadow-md hover:bg-[color:var(--app-card-hover)] active:shadow-sm ${isDragging ? 'opacity-70' : ''}`}
        onClick={onOpen}
      >
        <div className="flex items-center gap-2">
          <span className="text-[color:var(--app-text)] font-semibold text-base leading-snug clamp-3 flex-1">{list.title}</span>
        </div>
        <div className="mt-2 text-[color:var(--app-text-muted)] text-sm">
          {activeTasks.length} item{activeTasks.length !== 1 ? 's' : ''}
        </div>
      </div>
    );
  };

  // Normalize a user-entered URL (adds https:// if missing)
  const normalizeUrl = (u) => {
    try {
      const s = (u || "").trim();
      if (!s) return null;
      if (/^https?:\/\//i.test(s)) return s;
      return `https://${s}`;
    } catch {
      return null;
    }
  };

  // Derive a short site label from a URL (hostname, no www, remove TLD)
  const getSiteLabel = (u) => {
    try {
      const href = normalizeUrl(u);
      if (!href) return null;
      const host = new URL(href).hostname.replace(/^www\./i, '');
      return host.replace(/\.(com|org|net|io|co|us|uk)$/i, '') || null;
    } catch {
      return null;
    }
  };

  const getDueDateLabel = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [year, month, day] = parts;
    if (!year || !month || !day) return null;
    return `${month}/${day}/${String(year).slice(-2)}`;
  };

  const getDueDateFieldLabel = (value) => {
    if (!value || typeof value !== 'string') return '';
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return '';
    const [year, month, day] = parts;
    if (!year || !month || !day) return '';
    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(year, month - 1, day));
  };

  const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDateInputValue = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [year, month, day] = parts;
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const getDueDateCalendarMonth = (value) => {
    const date = parseDateInputValue(value) || new Date();
    return toDateInputValue(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  const shiftDueDateCalendarMonth = (monthValue, delta) => {
    const date = parseDateInputValue(monthValue) || new Date();
    return toDateInputValue(new Date(date.getFullYear(), date.getMonth() + delta, 1));
  };

  const getDueDateCalendarDays = (monthValue, selectedValue) => {
    const monthDate = parseDateInputValue(monthValue) || new Date();
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const todayValue = toDateInputValue(new Date());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(year, month, 1 - startOffset + index);
      const value = toDateInputValue(date);
      return {
        value,
        day: date.getDate(),
        inCurrentMonth: date.getMonth() === month,
        isSelected: value === selectedValue,
        isToday: value === todayValue
      };
    });
  };

  const renderTaskMetadataPills = (task) => {
    const dueDateLabel = getDueDateLabel(task?.details?.dueDate);
    const hasLink = !!task?.details?.link;

    if (!hasLink && !dueDateLabel) return null;

    return (
      <>
        {hasLink
          ? (() => {
              const href = normalizeUrl(task.details.link);
              if (!href) return null;
              return (
                <a
                  ref={el => {
                    if (el) linkRefs.current[task.id] = el;
                  }}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${
                    linkWrappedByTask[task.id] ? 'ml-0 mt-1 w-fit' : 'ml-3'
                  } inline-flex items-center justify-center px-2 h-5 rounded-full bg-[color:var(--app-chip-bg)] text-[color:var(--app-chip-text)] border border-[color:var(--app-chip-border)] text-[11px] leading-normal align-text-bottom hover:bg-[color:var(--app-surface-hover)] hover:text-[color:var(--app-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] transition-colors`}
                  title={href}
                >
                  <span className="inline-block max-w-[12ch] overflow-hidden text-ellipsis whitespace-nowrap">
                    {getSiteLabel(task.details.link) || 'link'}
                  </span>
                  <span className="sr-only">Open link</span>
                </a>
              );
            })()
          : null}
        {dueDateLabel ? (
          <span
            className={`${
              hasLink && linkWrappedByTask[task.id] ? 'ml-2 mt-1' : 'ml-3'
            } inline-flex items-center justify-center px-2 h-5 rounded-full bg-[color:var(--app-chip-bg)] text-[color:var(--app-chip-text)] border border-[color:var(--app-chip-border)] text-[11px] leading-normal align-text-bottom whitespace-nowrap`}
            title={`Due ${dueDateLabel}`}
          >
            {dueDateLabel}
          </span>
        ) : null}
      </>
    );
  };

  // ---- Location management helpers ----
  const isLocationInUse = (nickname) => {
    try {
      for (const l of lists) {
        if ((l.tasks || []).some(t => (t?.details?.locationNickname || "") === nickname)) return true;
      }
      return false;
    } catch { return false; }
  };

  const cascadeRenameLocation = (oldNick, newNick, newAddr) => {
    // 1) Update directory
    setLocations(prev => prev.map(l => l.nickname === oldNick ? { nickname: newNick, address: newAddr } : l));

    // 2) Update tasks in all lists
    setLists(prev => prev.map(list => ({
      ...list,
      tasks: (list.tasks || []).map(t => (
        (t?.details?.locationNickname || "") === oldNick
          ? { ...t, details: { ...(t.details || {}), locationNickname: newNick } }
          : t
      ))
    })));

    // 3) Update tasks in current view state
    setTasks(prev => prev.map(t => (
      (t?.details?.locationNickname || "") === oldNick
        ? { ...t, details: { ...(t.details || {}), locationNickname: newNick } }
        : t
    )));

    // 4) Rename per-location order keys and merge if necessary
    setLocationOrderByList(prev => {
      const next = { ...prev };
      Object.keys(next || {}).forEach(listId => {
        const byList = { ...(next[listId] || {}) };
        if (Object.prototype.hasOwnProperty.call(byList, oldNick)) {
          const moved = Array.isArray(byList[oldNick]) ? byList[oldNick] : [];
          const existing = Array.isArray(byList[newNick]) ? byList[newNick] : [];
          const merged = [...existing, ...moved.filter(id => !existing.includes(id))];
          delete byList[oldNick];
          byList[newNick] = merged;
          next[listId] = byList;
        }
      });
      return next;
    });
  };

  const cascadeDeleteLocation = (oldNick, reassignTo /* nickname or NO_LOCATION_KEY */) => {
    // 1) Update directory
    setLocations(prev => prev.filter(l => l.nickname !== oldNick));

    // 2) Update tasks across all lists
    setLists(prev => prev.map(list => ({
      ...list,
      tasks: (list.tasks || []).map(t => {
        if ((t?.details?.locationNickname || "") !== oldNick) return t;
        if (reassignTo && reassignTo !== NO_LOCATION_KEY) {
          return { ...t, details: { ...(t.details || {}), locationNickname: reassignTo } };
        }
        const { locationNickname, ...restDetails } = t.details || {};
        return { ...t, details: { ...restDetails, locationNickname: undefined } };
      })
    })));

    // 3) Update tasks in current view state
    setTasks(prev => prev.map(t => {
      if ((t?.details?.locationNickname || "") !== oldNick) return t;
      if (reassignTo && reassignTo !== NO_LOCATION_KEY) {
        return { ...t, details: { ...(t.details || {}), locationNickname: reassignTo } };
      }
      const { locationNickname, ...restDetails } = t.details || {};
      return { ...t, details: { ...restDetails, locationNickname: undefined } };
    }));

    // 4) Move/merge per-location order or delete key
    setLocationOrderByList(prev => {
      const next = { ...prev };
      Object.keys(next || {}).forEach(listId => {
        const byList = { ...(next[listId] || {}) };
        if (Object.prototype.hasOwnProperty.call(byList, oldNick)) {
          const moved = Array.isArray(byList[oldNick]) ? byList[oldNick] : [];
          delete byList[oldNick];
          if (reassignTo && reassignTo !== NO_LOCATION_KEY) {
            const existing = Array.isArray(byList[reassignTo]) ? byList[reassignTo] : [];
            byList[reassignTo] = [...existing, ...moved.filter(id => !existing.includes(id))];
          }
          next[listId] = byList;
        }
      });
      return next;
    });
  };

  const closeEditLocationToManage = () => {
    setShowEditLocationSheet(false);
    setEditLocError("");
    setConfirmingDeleteLoc(false);
    setDeleteReassignTo(NO_LOCATION_KEY);
    setReassignDropdownOpen(false);
    setShowManageLocationsSheet(true);
  };

  // Ensure completed tasks are below active within each location section; preserve relative order within groups
  // eslint-disable-next-line no-unused-vars
  const enforceCompletedAtBottomPerLocation = (listId) => {
    setLocationOrderByList(prev => {
      const prevByList = { ...(prev[listId] || {}) };

      // Build lookups from current view tasks
      const src = tasks; // current list view state already mirrors selected list
      const doneById = {};
      const locKeyById = {};
      src.forEach(t => {
        doneById[t.id] = !!t.done;
        locKeyById[t.id] = (t?.details?.locationNickname) || NO_LOCATION_KEY;
      });

      // Union of sections: existing keys + keys implied by tasks
      const sectionKeys = new Set([
        ...Object.keys(prevByList),
        ...src.map(t => locKeyById[t.id])
      ]);

      let changed = false;
      const nextByList = { ...prevByList };

      sectionKeys.forEach(key => {
        const baseIds = src.filter(t => locKeyById[t.id] === key).map(t => t.id);

        // Start from existing order but keep only valid ids and dedupe
        const existing = Array.isArray(prevByList[key]) ? prevByList[key] : [];
        const filtered = existing.filter(id => baseIds.includes(id));
        const dedup = [];
        filtered.forEach(id => { if (!dedup.includes(id)) dedup.push(id); });

        // Append any missing ids: actives appended at end of active block; dones appended to end
        let arr = dedup.slice();
        let boundary = arr.findIndex(id => doneById[id] === true);
        if (boundary === -1) boundary = arr.length; // end of active block
        baseIds.forEach(id => {
          if (arr.includes(id)) return;
          if (!doneById[id]) {
            // active → append at end of active block
            arr.splice(boundary, 0, id);
            boundary += 1; // active block grows
          } else {
            // done → append to end
            arr.push(id);
          }
        });

        // Stable partition just in case something crossed the boundary
        const actives = arr.filter(id => doneById[id] === false);
        const dones   = arr.filter(id => doneById[id] === true);
        const nextIds = [...actives, ...dones];

        const same = (nextByList[key] || []).length === nextIds.length && (nextByList[key] || []).every((v, i) => v === nextIds[i]);
        if (!same) { nextByList[key] = nextIds; changed = true; }
      });

      if (!changed) return prev;
      return { ...prev, [listId]: nextByList };
    });
  };

  // Push a task into Recently Deleted with origin list id and timestamp (dedupe by task.id)
  const recordRecentlyDeleted = (task, fromListId) => {
    if (!task) return;
    setRecentlyDeleted(prev => {
      // If an entry for this task.id already exists, do not add another
      if (prev.some(e => e?.task?.id === task.id)) return prev;
      const entry = { id: crypto.randomUUID(), task: { ...task }, fromListId, deletedAt: Date.now() };
      return [entry, ...prev]; // newest first
    });
  };

  // --------- Task handlers (within a list) ---------
  const addTask = () => {
    if (!input.trim()) return;
    const newTask = { id: crypto.randomUUID(), text: input, done: false, editing: false, details: {} };

    // 1) Update the flat tasks array (newest first)
    setTasks(prev => [newTask, ...prev]);

    // 2) If we're in Location sort for this list, make sure the new task sits at the TOP of the "No location" section
    const mode = sortModeByList[selectedListId];
    if (selectedListId && mode === 'location') {
      setLocationOrderByList(prevMap => {
        const byList = { ...(prevMap[selectedListId] || {}) };
        const key = NO_LOCATION_KEY; // new tasks default to no location
        const existing = Array.isArray(byList[key]) ? byList[key] : [];
        // Place new active task at the very start of the section
        const withoutDup = existing.filter(id => id !== newTask.id);
        byList[key] = [newTask.id, ...withoutDup];
        return { ...prevMap, [selectedListId]: byList };
      });
    }
    if (selectedListId && mode === 'project') {
      setProjectTopOrderByList(prevMap => {
        const existing = Array.isArray(prevMap[selectedListId]) ? prevMap[selectedListId] : [];
        return { ...prevMap, [selectedListId]: [newTask.id, ...existing.filter(id => id !== newTask.id)] };
      });
    }

    setInput("");
  };

  const finalizeTaskEditing = (taskId) => {
    const original = editOriginalTextRef.current;
    const current = tasks.find(t => t.id === taskId)?.text ?? "";
    if (original != null && current.trim() === "") {
      updateTask(taskId, 'text', original);
    }
    editOriginalTextRef.current = null;
    setEditingTaskId(null);
  };

  const startEditTask = (id) => {
    if (editDelayRef.current) {
      clearTimeout(editDelayRef.current);
      editDelayRef.current = null;
    }
    editOriginalTextRef.current = tasks.find(t => t.id === id)?.text ?? "";
    if (revealedInfoTaskId === id) {
      setRevealedInfoTaskId(null);
      editDelayRef.current = setTimeout(() => {
        setEditingTaskId(id);
        editDelayRef.current = null;
      }, 200);
      return;
    }
    setEditingTaskId(id);
  };

  const handleTaskPointerDown = (taskId, event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const rect = event.currentTarget?.getBoundingClientRect();
    const mode = selectedListId ? (sortModeByList[selectedListId] || 'priority') : 'priority';
    const projectRowType = event.currentTarget?.getAttribute?.('data-project-row-type');
    const revealThreshold = mode === 'project' && projectRowType === 'child' ? 64 : 30;
    dragPointerXRef.current = event.clientX;
    dragPointerYRef.current = event.clientY;
    dragPointerOffsetXRef.current = rect ? event.clientX - rect.left : 0;
    dragPointerOffsetYRef.current = rect ? event.clientY - rect.top : 0;
    dragCardHeightRef.current = rect?.height || 0;
    if (event.pointerType === 'mouse') return;
    taskSwipeRef.current = {
      taskId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastDx: 0,
      lastDy: 0,
      revealThreshold,
      pointerType: event.pointerType
    };
  };

  useEffect(() => {
    const updateDragPointer = (clientX, clientY) => {
      dragPointerXRef.current = clientX;
      dragPointerYRef.current = clientY;
    };

    const handleGlobalPointerMove = (event) => {
      updateDragPointer(event.clientX, event.clientY);
      const swipe = taskSwipeRef.current;
      if (!swipe || swipe.taskId == null) return;
      if (swipe.pointerId != null && event.pointerId !== swipe.pointerId) return;
      if (swipe.pointerType === 'mouse') return;
      if (isDraggingRef.current) return;
      const dx = event.clientX - swipe.startX;
      const dy = event.clientY - swipe.startY;
      swipe.lastDx = dx;
      swipe.lastDy = dy;
      if (Math.abs(dy) > Math.abs(dx)) return;
      const revealThreshold = swipe.revealThreshold || 30;
      if (dx < -revealThreshold) {
        if (editingTextareaRef.current && document.activeElement === editingTextareaRef.current) {
          editingTextareaRef.current.blur();
          const selection = window.getSelection();
          if (selection) selection.removeAllRanges();
          setEditingTaskId(null);
        } else {
          setEditingTaskId(prev => (prev === swipe.taskId ? null : prev));
        }
        setRevealedInfoTaskId(swipe.taskId);
      } else if (dx > 12 || dx > -(revealThreshold / 2)) {
        setRevealedInfoTaskId(prev => (prev === swipe.taskId ? null : prev));
      }
    };

    const handleGlobalMouseMove = (event) => {
      updateDragPointer(event.clientX, event.clientY);
    };

    const handleGlobalTouchMove = (event) => {
      const touch = event.touches?.[0] || event.changedTouches?.[0];
      if (!touch) return;
      updateDragPointer(touch.clientX, touch.clientY);
    };

    const handleGlobalPointerEnd = (event) => {
      const swipe = taskSwipeRef.current;
      if (!swipe || swipe.taskId == null) return;
      if (swipe.pointerType === 'mouse') return;
      const dx = event.clientX - swipe.startX;
      const dy = event.clientY - swipe.startY;
      const isHorizontal = Math.abs(dx) > Math.abs(dy);
      const revealThreshold = swipe.revealThreshold || 30;
      if (!isDraggingRef.current && isHorizontal && Math.abs(dx) > revealThreshold) {
        if (dx < 0) {
          if (editingTextareaRef.current && document.activeElement === editingTextareaRef.current) {
            editingTextareaRef.current.blur();
            const selection = window.getSelection();
            if (selection) selection.removeAllRanges();
            setEditingTaskId(null);
          } else {
            setEditingTaskId(prev => (prev === swipe.taskId ? null : prev));
          }
          setRevealedInfoTaskId(swipe.taskId);
        } else {
          setRevealedInfoTaskId(prev => (prev === swipe.taskId ? null : prev));
        }
      }
      taskSwipeRef.current = null;
    };

    window.addEventListener('pointermove', handleGlobalPointerMove);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });
    window.addEventListener('pointerup', handleGlobalPointerEnd);
    window.addEventListener('pointercancel', handleGlobalPointerEnd);
    return () => {
      window.removeEventListener('pointermove', handleGlobalPointerMove);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('pointerup', handleGlobalPointerEnd);
      window.removeEventListener('pointercancel', handleGlobalPointerEnd);
    };
  }, []);

  const updateTask = (id, key, value) => {
    setTasks(prev =>
      prev.map(task => {
        if (task.id !== id) return task;
        if (task[key] === value) return task;
        return { ...task, [key]: value };
      })
    );
  };

  const startAutoScrollLoop = () => {
    if (dragAutoScrollRafRef.current) return;
    const step = () => {
      if (!isDraggingRef.current) {
        dragAutoScrollRafRef.current = null;
        return;
      }
      const scrollEl = listScrollRef.current;
      if (scrollEl) {
        const rect = scrollEl.getBoundingClientRect();
        const y = dragPointerYRef.current;
        const edgeThreshold = 120;
        const minSpeed = 4;
        const maxSpeed = 18;
        const scrollRange = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (scrollRange > 0) {
          const bottomDistance = rect.bottom - y;
          const topDistance = y - rect.top;
          if (bottomDistance < edgeThreshold && scrollEl.scrollTop < scrollRange) {
            const intensity = Math.max(0, Math.min(1, (edgeThreshold - bottomDistance) / edgeThreshold));
            scrollEl.scrollTop += minSpeed + (maxSpeed - minSpeed) * intensity;
          } else if (topDistance < edgeThreshold && scrollEl.scrollTop > 0) {
            const intensity = Math.max(0, Math.min(1, (edgeThreshold - topDistance) / edgeThreshold));
            scrollEl.scrollTop -= minSpeed + (maxSpeed - minSpeed) * intensity;
          }
        }
      }
      dragAutoScrollRafRef.current = requestAnimationFrame(step);
    };
    dragAutoScrollRafRef.current = requestAnimationFrame(step);
  };

  const stopAutoScrollLoop = () => {
    if (dragAutoScrollRafRef.current) {
      cancelAnimationFrame(dragAutoScrollRafRef.current);
      dragAutoScrollRafRef.current = null;
    }
  };

  const deleteTask = id => {
    setTasks(prev => {
      const t = prev.find(x => x.id === id);
      if (t) recordRecentlyDeleted(t, selectedListId);
      return prev
        .filter(task => task.id !== id)
        .map(task => (
          task?.details?.projectTaskId === id
            ? { ...task, details: { ...(task.details || {}), projectTaskId: undefined } }
            : task
        ));
    });
    setProjectTopOrderByList(prev => ({
      ...prev,
      [selectedListId]: (prev[selectedListId] || []).filter(taskId => taskId !== id)
    }));
    setProjectChildOrderByList(prev => {
      const byList = { ...(prev[selectedListId] || {}) };
      delete byList[id];
      Object.keys(byList).forEach(parentId => {
        byList[parentId] = (byList[parentId] || []).filter(taskId => taskId !== id);
      });
      return { ...prev, [selectedListId]: byList };
    });
    setCollapsedProjectsByList(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(listId => {
        if (!next[listId]?.[id]) return;
        const byProject = { ...next[listId] };
        delete byProject[id];
        next[listId] = byProject;
      });
      return next;
    });
    setOpenDialogId(null);
  };

  // Drop completed tasks to bottom globally, and also normalize order within each location section when in Location sort
  const toggleTask = id => {
    setTasks(prev => {
      const t = prev.find(task => task.id === id);
      if (!t) return prev;
      const rest = prev.filter(task => task.id !== id);
      const updated = { ...t, done: !t.done };
      const active = rest.filter(task => !task.done);
      const done = rest.filter(task => task.done);
      const nextTasks = updated.done ? [...active, ...done, updated] : [updated, ...active, ...done];

      // If we're in Location sort, move this task id within its section array
      const mode = sortModeByList[selectedListId];
      if (selectedListId && mode === 'location') {
        const locKey = (updated?.details?.locationNickname) || NO_LOCATION_KEY;
        setLocationOrderByList(prevMap => {
          const byList = { ...(prevMap[selectedListId] || {}) };
          const existing = Array.isArray(byList[locKey]) ? byList[locKey] : [];

          // Build the complete set of ids for this section based on the *updated* task list
          const sectionIds = nextTasks
            .filter(tt => ((tt?.details?.locationNickname) || NO_LOCATION_KEY) === locKey)
            .map(tt => tt.id);

          // Merge: keep stored order first, then any missing ids in their current (visual) order
          const merged = [
            ...existing.filter(id => sectionIds.includes(id)),
            ...sectionIds.filter(id => !existing.includes(id))
          ];

          // Remove the toggled id from the merged list
          const without = merged.filter(tid => tid !== id);

          if (updated.done) {
            // Completed → push to end of that location section (bottom of completed block)
            without.push(id);
          } else {
            // Active → insert just before the first completed within that section
            const doneById = {};
            nextTasks.forEach(tt => { doneById[tt.id] = !!tt.done; });
            let boundary = without.findIndex(tid => doneById[tid] === true);
            if (boundary === -1) boundary = without.length;
            without.splice(boundary, 0, id);
          }

          return { ...prevMap, [selectedListId]: { ...byList, [locKey]: without } };
        });
      }
      if (selectedListId && mode === 'project') {
        const parentId = updated?.details?.projectTaskId || null;
        const moveWithin = (ids) => {
          const without = ids.filter(tid => tid !== id);
          const doneById = {};
          nextTasks.forEach(tt => { doneById[tt.id] = !!tt.done; });
          if (updated.done) {
            without.push(id);
          } else {
            let boundary = without.findIndex(tid => doneById[tid] === true);
            if (boundary === -1) boundary = without.length;
            without.splice(boundary, 0, id);
          }
          return without;
        };
        if (parentId) {
          setProjectChildOrderByList(prevMap => {
            const byList = { ...(prevMap[selectedListId] || {}) };
            const sectionIds = nextTasks.filter(tt => tt?.details?.projectTaskId === parentId).map(tt => tt.id);
            const existing = Array.isArray(byList[parentId]) ? byList[parentId] : [];
            const merged = [
              ...existing.filter(taskId => sectionIds.includes(taskId)),
              ...sectionIds.filter(taskId => !existing.includes(taskId))
            ];
            return { ...prevMap, [selectedListId]: { ...byList, [parentId]: moveWithin(merged) } };
          });
        } else {
          setProjectTopOrderByList(prevMap => {
            const sectionIds = nextTasks.filter(tt => !tt?.details?.projectTaskId).map(tt => tt.id);
            const existing = Array.isArray(prevMap[selectedListId]) ? prevMap[selectedListId] : [];
            const merged = [
              ...existing.filter(taskId => sectionIds.includes(taskId)),
              ...sectionIds.filter(taskId => !existing.includes(taskId))
            ];
            return { ...prevMap, [selectedListId]: moveWithin(merged) };
          });
        }
      }

      return nextTasks;
    });
  };

  // Animate check then toggle completion
  const handleCheck = id => {
    setAnimatingTask(id);
    setTimeout(() => {
      toggleTask(id);
      setAnimatingTask(null);
    }, Math.round(CROSS_OUT_DURATION * 1000) + 60);
  };

  // Delete all completed tasks (and record them for recovery)
  const deleteCompleted = () => setTasks(prev => {
    const completed = prev.filter(t => t.done);
    if (completed.length) completed.forEach(t => recordRecentlyDeleted(t, selectedListId));
    return prev.filter(t => !t.done);
  });

  const visibleTasks = hideCompleted ? tasks.filter(task => !task.done) : tasks;
  const activeTaskIds = new Set(tasks.map(t => t.id));
  const getProjectParentId = (task) => {
    const parentId = task?.details?.projectTaskId;
    if (!parentId || parentId === task?.id || !activeTaskIds.has(parentId)) return null;
    return parentId;
  };
  const getProjectChildren = (parentId, sourceTasks = tasks) => (
    sourceTasks.filter(t => getProjectParentId(t) === parentId)
  );
  const isProjectParent = (taskId, sourceTasks = tasks) => (
    sourceTasks.some(t => {
      const parentId = t?.details?.projectTaskId;
      return parentId === taskId && parentId !== t.id && sourceTasks.some(p => p.id === parentId);
    })
  );
  const projectDoneById = new Map(tasks.map(t => [t.id, !!t.done]));
  const partitionProjectIds = (ids) => {
    const unique = [];
    ids.forEach(id => {
      if (activeTaskIds.has(id) && !unique.includes(id)) unique.push(id);
    });
    return [
      ...unique.filter(id => !projectDoneById.get(id)),
      ...unique.filter(id => projectDoneById.get(id))
    ];
  };
  const getProjectTopOrder = (sourceTasks = tasks) => {
    const topIds = sourceTasks
      .filter(t => !getProjectParentId(t))
      .filter(t => !(hideCompleted && t.done))
      .filter(t => !(hideCompleted && isProjectParent(t.id, sourceTasks) && t.done))
      .map(t => t.id);
    const stored = Array.isArray(projectTopOrderByList[selectedListId])
      ? projectTopOrderByList[selectedListId]
      : [];
    return partitionProjectIds([
      ...stored.filter(id => topIds.includes(id)),
      ...topIds.filter(id => !stored.includes(id))
    ]).filter(id => topIds.includes(id));
  };
  const getProjectChildOrder = (parentId, sourceTasks = tasks) => {
    const childIds = sourceTasks
      .filter(t => getProjectParentId(t) === parentId)
      .filter(t => !(hideCompleted && t.done))
      .map(t => t.id);
    const byList = projectChildOrderByList[selectedListId] || {};
    const stored = Array.isArray(byList[parentId]) ? byList[parentId] : [];
    return partitionProjectIds([
      ...stored.filter(id => childIds.includes(id)),
      ...childIds.filter(id => !stored.includes(id))
    ]).filter(id => childIds.includes(id));
  };
  const getProjectRows = () => {
    const byId = new Map(tasks.map(t => [t.id, t]));
    return getProjectTopOrder(tasks)
      .map(id => byId.get(id))
      .filter(Boolean)
      .map(task => ({
        task,
        isParent: isProjectParent(task.id),
        children: getProjectChildOrder(task.id, tasks).map(id => byId.get(id)).filter(Boolean)
      }))
      .filter(row => !(hideCompleted && row.task.done));
  };
  const getProjectOutlineRows = () => {
    const rows = [];
    getProjectRows().forEach(row => {
      rows.push({
        type: 'top',
        task: row.task,
        parentId: null,
        isParent: row.isParent
      });
      if (row.isParent && !collapsedProjects[row.task.id]) {
        row.children.forEach(child => {
          rows.push({
            type: 'child',
            task: child,
            parentId: row.task.id,
            isParent: false
          });
        });
      }
    });
    return rows;
  };
  const saveProjectTopOrder = (ids) => {
    setProjectTopOrderByList(prev => ({ ...prev, [selectedListId]: partitionProjectIds(ids) }));
  };
  const saveProjectChildOrder = (parentId, ids) => {
    setProjectChildOrderByList(prev => ({
      ...prev,
      [selectedListId]: {
        ...(prev[selectedListId] || {}),
        [parentId]: partitionProjectIds(ids)
      }
    }));
  };
  const unassignFromProject = (taskId) => {
    setTasks(prev => prev.map(t => (
      t.id === taskId
        ? { ...t, details: { ...(t.details || {}), projectTaskId: undefined } }
        : t
    )));
  };
  const assignTaskToProject = (taskId, parentId) => {
    if (!taskId) return;
    const currentParent = tasks.find(t => t.id === taskId)?.details?.projectTaskId || null;
    if (!parentId) {
      unassignFromProject(taskId);
      setProjectTopOrderByList(prev => {
        const existing = Array.isArray(prev[selectedListId]) ? prev[selectedListId] : [];
        return { ...prev, [selectedListId]: [taskId, ...existing.filter(id => id !== taskId)] };
      });
      if (currentParent) {
        setProjectChildOrderByList(prev => ({
          ...prev,
          [selectedListId]: {
            ...(prev[selectedListId] || {}),
            [currentParent]: ((prev[selectedListId] || {})[currentParent] || []).filter(id => id !== taskId)
          }
        }));
      }
      return;
    }
    if (taskId === parentId || isProjectParent(taskId)) return;
    setTasks(prev => prev.map(t => {
      if (t.id === taskId) return { ...t, details: { ...(t.details || {}), projectTaskId: parentId } };
      if (t.id === parentId && t.details?.projectTaskId) {
        const { projectTaskId, ...restDetails } = t.details || {};
        return { ...t, details: restDetails };
      }
      return t;
    }));
    setProjectTopOrderByList(prev => ({
      ...prev,
      [selectedListId]: (prev[selectedListId] || []).filter(id => id !== taskId)
    }));
    setProjectChildOrderByList(prev => {
      const byList = { ...(prev[selectedListId] || {}) };
      if (currentParent) byList[currentParent] = (byList[currentParent] || []).filter(id => id !== taskId);
      byList[parentId] = [taskId, ...(byList[parentId] || []).filter(id => id !== taskId)];
      return { ...prev, [selectedListId]: byList };
    });
  };
  const addSubtask = (parentId) => {
    const text = subtaskInput.trim();
    if (!text || !parentId) return;
    const newTask = {
      id: crypto.randomUUID(),
      text,
      done: false,
      editing: false,
      details: { projectTaskId: parentId }
    };
    setTasks(prev => [newTask, ...prev.map(t => (
      t.id === parentId && t.details?.projectTaskId
        ? { ...t, details: { ...(t.details || {}), projectTaskId: undefined } }
        : t
    ))]);
    setProjectChildOrderByList(prev => {
      const byList = { ...(prev[selectedListId] || {}) };
      const existing = Array.isArray(byList[parentId]) ? byList[parentId] : [];
      return {
        ...prev,
        [selectedListId]: {
          ...byList,
          [parentId]: [newTask.id, ...existing.filter(id => id !== newTask.id)]
        }
      };
    });
    setSubtaskInput('');
    updateCollapsedProjects(prev => ({ ...prev, [parentId]: false }));
  };

  const canDropTaskOntoProject = (draggedId, targetId) => {
    if (!draggedId || !targetId || draggedId === targetId) return false;
    const dragged = tasks.find(t => t.id === draggedId);
    const target = tasks.find(t => t.id === targetId);
    if (!dragged || !target) return false;
    return !getProjectParentId(target) && !isProjectParent(dragged.id);
  };

  const setProjectDropTarget = (targetId) => {
    const nextTargetId = targetId || null;
    projectDropTargetRef.current = nextTargetId;
    setProjectDropTargetId(prev => (prev === nextTargetId ? prev : nextTargetId));
  };

  const getProjectActiveDraggedRect = (draggedId) => {
    if (!draggedId || typeof document === 'undefined') return null;
    const escapedId = window.CSS?.escape ? window.CSS.escape(draggedId) : draggedId;
    const candidates = Array.from(document.querySelectorAll(`[data-rfd-draggable-id="${escapedId}"]`));
    const activeDragEl = candidates.find(el => window.getComputedStyle(el).position === 'fixed');
    if (!activeDragEl) return null;
    const rect = activeDragEl.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? rect : null;
  };

  const getProjectDraggedCardLeft = (draggedId) => {
    return dragPointerXRef.current - dragPointerOffsetXRef.current;
  };

  const getProjectDraggedCardVerticalBounds = (draggedId) => {
    const activeRect = getProjectActiveDraggedRect(draggedId);
    if (activeRect) return { top: activeRect.top, bottom: activeRect.bottom };
    const top = dragPointerYRef.current - dragPointerOffsetYRef.current;
    const height = Math.max(1, dragCardHeightRef.current || 0);
    return { top, bottom: top + height };
  };

  const isDraggingInProjectChildLane = (draggedId) => {
    if (typeof document === 'undefined') return false;
    const topRowEl = document.querySelector('[data-project-row-type="top"]');
    const childRowEl = document.querySelector('[data-project-row-type="child"]');
    if (!topRowEl || !childRowEl) return false;
    const topRowLeft = topRowEl.getBoundingClientRect().left;
    const childRowLeft = childRowEl.getBoundingClientRect().left;
    const topLevelLaneMaxX = topRowLeft + Math.max(16, (childRowLeft - topRowLeft) / 2);
    const draggedCardLeft = getProjectDraggedCardLeft(draggedId);
    return draggedCardLeft > topLevelLaneMaxX;
  };

  const getProjectDropTargetFromOverlappedChildBlock = (draggedId) => {
    if (!draggedId || typeof document === 'undefined') return null;
    if (!isDraggingInProjectChildLane(draggedId)) return null;
    const childRows = Array.from(document.querySelectorAll('[data-project-row-type="child"][data-project-parent-id]'))
      .filter(row => row.getAttribute('data-project-task-id') !== draggedId);
    if (!childRows.length) return null;

    const { top: draggedTop, bottom: draggedBottom } = getProjectDraggedCardVerticalBounds(draggedId);
    const blockByParent = childRows.reduce((map, row) => {
      const parentId = row.getAttribute('data-project-parent-id');
      if (!parentId || !canDropTaskOntoProject(draggedId, parentId)) return map;
      const rect = row.getBoundingClientRect();
      const block = map.get(parentId) || { top: Number.POSITIVE_INFINITY, bottom: Number.NEGATIVE_INFINITY };
      block.top = Math.min(block.top, rect.top);
      block.bottom = Math.max(block.bottom, rect.bottom);
      map.set(parentId, block);
      return map;
    }, new Map());

    let bestParentId = null;
    let bestOverlap = 0;
    const cushion = 18;
    blockByParent.forEach((block, parentId) => {
      const top = block.top - cushion;
      const bottom = block.bottom + cushion;
      const overlap = Math.min(draggedBottom, bottom) - Math.max(draggedTop, top);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestParentId = parentId;
      }
    });

    return bestParentId;
  };

  const getProjectChildInsertIndex = (parentId, draggedId, destination) => {
    if (destination) {
      const outlineRows = getProjectOutlineRows();
      const rowsWithoutDragged = outlineRows.filter(row => row.task.id !== draggedId);
      return rowsWithoutDragged
        .slice(0, destination.index)
        .filter(row => row.type === 'child' && row.parentId === parentId)
        .length;
    }

    if (typeof document === 'undefined') return 0;
    const childRows = Array.from(document.querySelectorAll(`[data-project-row-type="child"][data-project-parent-id="${parentId}"]`))
      .filter(row => row.getAttribute('data-project-task-id') !== draggedId)
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    if (!childRows.length) return 0;

    const { top, bottom } = getProjectDraggedCardVerticalBounds(draggedId);
    const draggedCenterY = (top + bottom) / 2;
    const insertIndex = childRows.findIndex(row => {
      const rect = row.getBoundingClientRect();
      return draggedCenterY < rect.top + rect.height / 2;
    });
    return insertIndex === -1 ? childRows.length : insertIndex;
  };

  const getProjectDropTargetFromTaskId = (draggedId, targetTaskId) => {
    if (!targetTaskId || targetTaskId === draggedId) return null;
    const outlineRows = getProjectOutlineRows();
    const targetRow = outlineRows.find(row => row.task.id === targetTaskId);
    const draggedRow = outlineRows.find(row => row.task.id === draggedId);
    if (targetRow?.type === 'child' && !isDraggingInProjectChildLane(draggedId)) return null;
    if (targetRow?.type === 'top' && draggedRow?.type === 'child' && !isDraggingInProjectChildLane(draggedId)) return null;
    const parentId = targetRow?.type === 'child' ? targetRow.parentId : targetTaskId;
    return canDropTaskOntoProject(draggedId, parentId) ? parentId : null;
  };

  const findProjectDropTargetFromPointer = (draggedId) => {
    if (typeof document === 'undefined') return null;
    const elements = document.elementsFromPoint(dragPointerXRef.current, dragPointerYRef.current);
    const seenRows = new Set();
    for (const el of elements) {
      const targetRow = el.closest?.('[data-project-task-id]');
      if (!targetRow || seenRows.has(targetRow)) continue;
      seenRows.add(targetRow);
      const targetTaskId = targetRow.getAttribute('data-project-task-id');
      if (!targetTaskId || targetTaskId === draggedId) continue;
      const rowType = targetRow.getAttribute('data-project-row-type');
      if (rowType === 'child' && !isDraggingInProjectChildLane(draggedId)) continue;
      const parentId = rowType === 'child'
        ? targetRow.getAttribute('data-project-parent-id')
        : targetTaskId;
      if (canDropTaskOntoProject(draggedId, parentId)) return parentId;
    }
    return null;
  };

  const isPointerOverProjectTopTask = (taskId) => {
    if (!taskId || typeof document === 'undefined') return false;
    return document.elementsFromPoint(dragPointerXRef.current, dragPointerYRef.current).some(el => {
      const row = el.closest?.('[data-project-task-id]');
      return row?.getAttribute('data-project-task-id') === taskId &&
        row?.getAttribute('data-project-row-type') === 'top';
    });
  };

  const getProjectDropTargetFromDestination = (draggedId, destination) => {
    if (!draggedId || !destination) return null;
    const outlineRows = getProjectOutlineRows();
    const rowsWithoutDragged = outlineRows.filter(row => row.task.id !== draggedId);
    const beforeDrop = rowsWithoutDragged[destination.index - 1] || null;
    const afterDrop = rowsWithoutDragged[destination.index] || null;
    if (!isDraggingInProjectChildLane(draggedId)) return null;

    if (beforeDrop?.type === 'child' && afterDrop?.type === 'child' && beforeDrop.parentId === afterDrop.parentId) {
      return canDropTaskOntoProject(draggedId, beforeDrop.parentId) ? beforeDrop.parentId : null;
    }

    if (beforeDrop?.type === 'top' && afterDrop?.type === 'child' && afterDrop.parentId === beforeDrop.task.id) {
      return canDropTaskOntoProject(draggedId, beforeDrop.task.id) ? beforeDrop.task.id : null;
    }

    if (beforeDrop?.type === 'child' && (!afterDrop || afterDrop.type === 'top')) {
      return canDropTaskOntoProject(draggedId, beforeDrop.parentId) ? beforeDrop.parentId : null;
    }

    const previousChild = (() => {
      for (let i = destination.index - 1; i >= 0; i -= 1) {
        const row = rowsWithoutDragged[i];
        if (!row) break;
        if (row.type === 'child') return row;
        if (row.type === 'top') break;
      }
      return null;
    })();
    const nextChild = (() => {
      for (let i = destination.index; i < rowsWithoutDragged.length; i += 1) {
        const row = rowsWithoutDragged[i];
        if (!row) break;
        if (row.type === 'child') return row;
        if (row.type === 'top') break;
      }
      return null;
    })();

    const parentId = previousChild?.parentId || nextChild?.parentId || null;
    if (!parentId) return null;
    if (previousChild && nextChild && previousChild.parentId !== nextChild.parentId) return null;
    return canDropTaskOntoProject(draggedId, parentId) ? parentId : null;
  };

  const handleDragEnd = result => {
    const { source, destination, combine, draggableId } = result;
    const mode = sortModeByList[selectedListId];
    if (!destination && !combine && !(mode === 'project' && projectDropTargetRef.current)) return;

    if (mode === 'project') {
      const draggableTaskId = draggableId;
      if (!draggableTaskId) return;
      const activeProjectDropTargetId = projectDropTargetRef.current;
      const overlappedProjectDropTargetId = getProjectDropTargetFromOverlappedChildBlock(draggableTaskId);
      const combineProjectDropTargetId = getProjectDropTargetFromTaskId(draggableTaskId, combine?.draggableId);
      const destinationProjectDropTargetId = getProjectDropTargetFromDestination(draggableTaskId, destination);
      const resolvedProjectDropTargetId = (
        activeProjectDropTargetId &&
        activeProjectDropTargetId !== draggableTaskId &&
        canDropTaskOntoProject(draggableTaskId, activeProjectDropTargetId) &&
        (
          overlappedProjectDropTargetId === activeProjectDropTargetId ||
          combineProjectDropTargetId === activeProjectDropTargetId ||
          isPointerOverProjectTopTask(activeProjectDropTargetId)
        )
      )
        ? activeProjectDropTargetId
        : (overlappedProjectDropTargetId || combineProjectDropTargetId || destinationProjectDropTargetId);
      if (
        resolvedProjectDropTargetId &&
        resolvedProjectDropTargetId !== draggableTaskId &&
        canDropTaskOntoProject(draggableTaskId, resolvedProjectDropTargetId)
      ) {
        assignTaskToProject(draggableTaskId, resolvedProjectDropTargetId);
        const top = getProjectTopOrder().filter(id => id !== draggableTaskId);
        saveProjectTopOrder(top.includes(resolvedProjectDropTargetId) ? top : [...top, resolvedProjectDropTargetId]);
        const childInsertIndex = getProjectChildInsertIndex(resolvedProjectDropTargetId, draggableTaskId, destination);
        const nextChildren = getProjectChildOrder(resolvedProjectDropTargetId).filter(id => id !== draggableTaskId);
        nextChildren.splice(Math.min(childInsertIndex, nextChildren.length), 0, draggableTaskId);
        saveProjectChildOrder(resolvedProjectDropTargetId, nextChildren);
        return;
      }
      if (combine?.draggableId && combine.draggableId !== draggableTaskId) {
        const target = tasks.find(t => t.id === combine.draggableId);
        const dragged = tasks.find(t => t.id === draggableTaskId);
        const outlineRows = getProjectOutlineRows();
        const targetRow = outlineRows.find(row => row.task.id === combine.draggableId);
        const draggedRow = outlineRows.find(row => row.task.id === draggableTaskId);
        let handledCombine = false;
        if (target && dragged && draggedRow?.type === 'top' && isProjectParent(dragged.id) && (targetRow?.type === 'top' || targetRow?.type === 'child')) {
          const targetTopId = targetRow.type === 'child' ? targetRow.parentId : target.id;
          const topOrder = getProjectTopOrder();
          const targetIndex = topOrder.indexOf(targetTopId);
          const draggedIndex = topOrder.indexOf(dragged.id);
          if (targetIndex !== -1 && draggedIndex !== -1) {
            const nextTop = topOrder.filter(id => id !== dragged.id);
            const insertIndex = Math.max(0, targetIndex + (draggedIndex < targetIndex ? 1 : 0));
            nextTop.splice(Math.min(insertIndex, nextTop.length), 0, dragged.id);
            saveProjectTopOrder(nextTop);
            handledCombine = true;
          }
        } else if (
          target &&
          dragged &&
          !getProjectParentId(target) &&
          !isProjectParent(dragged.id) &&
          !(draggedRow?.type === 'child' && targetRow?.type === 'top' && !isDraggingInProjectChildLane(dragged.id))
        ) {
          assignTaskToProject(dragged.id, target.id);
          const top = getProjectTopOrder().filter(id => id !== dragged.id);
          saveProjectTopOrder(top.includes(target.id) ? top : [...top, target.id]);
          saveProjectChildOrder(target.id, [dragged.id, ...getProjectChildOrder(target.id).filter(id => id !== dragged.id)]);
          handledCombine = true;
        }
        if (handledCombine) return;
      }
      if (!destination) return;

      const outlineRows = getProjectOutlineRows();
      const fromRow = outlineRows.find(row => row.task.id === draggableTaskId);
      if (!fromRow) return;

      const fromTop = fromRow.type === 'top';
      const fromParent = fromRow.parentId;
      const draggingProjectParent = isProjectParent(draggableTaskId);
      const rowsWithoutDragged = outlineRows.filter(row => row.task.id !== draggableTaskId);
      const beforeDrop = rowsWithoutDragged[destination.index - 1] || null;
      const afterDrop = rowsWithoutDragged[destination.index] || null;

      let toTop = true;
      let toParent = null;
      let destinationIndex = rowsWithoutDragged.slice(0, destination.index).filter(row => row.type === 'top').length;

      const sourceChildIdsForBoundary = fromTop ? [] : getProjectChildOrder(fromParent);
      const topRowEl = document.querySelector('[data-project-row-type="top"]');
      const childRowEl = document.querySelector('[data-project-row-type="child"]');
      const topRowLeft = topRowEl?.getBoundingClientRect().left ?? 0;
      const childRowLeft = childRowEl?.getBoundingClientRect().left ?? topRowLeft + 32;
      const topLevelLaneMaxX = topRowLeft + Math.max(16, (childRowLeft - topRowLeft) / 2);
      const draggedCardLeft = getProjectDraggedCardLeft(draggableTaskId);
      const isDroppingInTopLevelLane = draggedCardLeft <= topLevelLaneMaxX;
      const isChildToTopBoundary = (
        !fromTop &&
        beforeDrop?.type === 'child' &&
        (afterDrop?.type === 'top' || !afterDrop) &&
        isDroppingInTopLevelLane &&
        (source.index < destination.index || fromParent !== beforeDrop.parentId || sourceChildIdsForBoundary[sourceChildIdsForBoundary.length - 1] === draggableTaskId)
      );
      const isDownwardChildToTopBoundary = (
        !fromTop &&
        source.index < destination.index &&
        beforeDrop?.type === 'top' &&
        afterDrop?.type === 'child' &&
        afterDrop.parentId === beforeDrop.task.id
      );

      if (beforeDrop?.type === 'child' && !isDroppingInTopLevelLane && !isChildToTopBoundary && !(fromTop && draggingProjectParent)) {
        toTop = false;
        toParent = beforeDrop.parentId;
        destinationIndex = rowsWithoutDragged
          .slice(0, destination.index)
          .filter(row => row.type === 'child' && row.parentId === toParent)
          .length;
      } else if (isDownwardChildToTopBoundary) {
        destinationIndex = rowsWithoutDragged
          .slice(0, Math.max(destination.index - 1, 0))
          .filter(row => row.type === 'top')
          .length;
      } else if (beforeDrop?.type === 'top' && afterDrop?.type === 'child' && afterDrop.parentId === beforeDrop.task.id && !isDroppingInTopLevelLane && !(fromTop && draggingProjectParent)) {
        toTop = false;
        toParent = beforeDrop.task.id;
        destinationIndex = 0;
      } else if (afterDrop?.type === 'child' && !isDroppingInTopLevelLane && !(fromTop && draggingProjectParent)) {
        toTop = false;
        toParent = afterDrop.parentId;
        destinationIndex = rowsWithoutDragged
          .slice(0, destination.index)
          .filter(row => row.type === 'child' && row.parentId === toParent)
          .length;
      }

      if (!toTop && (draggableTaskId === toParent || isProjectParent(draggableTaskId))) return;

      const srcIds = fromTop ? getProjectTopOrder() : getProjectChildOrder(fromParent);
      const dstIds = fromTop === toTop && fromParent === toParent ? srcIds : (toTop ? getProjectTopOrder() : getProjectChildOrder(toParent));
      const nextSrc = srcIds.filter(id => id !== draggableTaskId);
      const nextDst = fromTop === toTop && fromParent === toParent ? nextSrc : dstIds.filter(id => id !== draggableTaskId);
      nextDst.splice(Math.min(destinationIndex, nextDst.length), 0, draggableTaskId);

      if (fromTop) saveProjectTopOrder(nextSrc);
      else saveProjectChildOrder(fromParent, nextSrc);
      if (toTop) saveProjectTopOrder(nextDst);
      else saveProjectChildOrder(toParent, nextDst);

      setTasks(prev => prev.map(t => {
        if (t.id === draggableTaskId) {
          const details = { ...(t.details || {}) };
          if (toTop) delete details.projectTaskId;
          else details.projectTaskId = toParent;
          return { ...t, details };
        }
        if (!toTop && t.id === toParent && t.details?.projectTaskId) {
          const { projectTaskId, ...restDetails } = t.details || {};
          return { ...t, details: restDetails };
        }
        return t;
      }));
      return;
    }

    const decodeKey = (id) => {
      if (!id) return NO_LOCATION_KEY;
      if (id.startsWith('loc:')) return normalizeLocKey(id.slice(4));
      if (id.startsWith('section:')) return normalizeLocKey(id.slice(8));
      return normalizeLocKey(id);
    };

    if (mode === 'location') {
      const srcKey = decodeKey(source.droppableId);
      const dstKey = decodeKey(destination.droppableId);

      const getSectionOrder = (key) => {
        const k = normalizeLocKey(key);
        const byList = locationOrderByList[selectedListId] || {};
        const stored = Array.isArray(byList[k]) ? byList[k] : [];
        const fromTasks = tasks
          .filter(t => (((t?.details?.locationNickname) || NO_LOCATION_KEY)) === k)
          .map(t => t.id);
        // Prefer stored order, but for the NO_LOCATION section, put any missing ACTIVE items at the top
        if (k === NO_LOCATION_KEY) {
          const doneById = {};
          tasks.forEach(t => { doneById[t.id] = !!t.done; });
          const missing = fromTasks.filter(id => !stored.includes(id));
          const missingActive = missing.filter(id => !doneById[id]);
          const missingDone = missing.filter(id => doneById[id]);
          return [
            ...missingActive,
            ...stored.filter(id => fromTasks.includes(id)),
            ...missingDone
          ];
        }
        return [
          ...stored.filter(id => fromTasks.includes(id)),
          ...fromTasks.filter(id => !stored.includes(id))
        ];
      };

      const beforeSrc = getSectionOrder(srcKey);
      const beforeDst = (srcKey === dstKey) ? beforeSrc : getSectionOrder(dstKey);

      const movedId = beforeSrc[source.index];
      const doneById = {};
      tasks.forEach(t => { doneById[t.id] = !!t.done; });
      const isMovedDone = !!doneById[movedId];

      const firstDoneIdx = (arr) => {
        const idx = arr.findIndex(id => doneById[id] === true);
        return idx === -1 ? arr.length : idx;
      };

      const clampToPartition = (arr) => {
        const a = arr.filter(id => doneById[id] === false);
        const d = arr.filter(id => doneById[id] === true);
        return [...a, ...d];
      };

      // Same-section move
      if (srcKey === dstKey) {
        const next = beforeSrc.slice();
        const [removed] = next.splice(source.index, 1);

        if (isMovedDone) {
          // Completed task: clamp to at/after the first completed index within the section
          const boundary = firstDoneIdx(next);
          const destIndex = Math.max(destination.index, boundary);
          next.splice(Math.min(destIndex, next.length), 0, removed);
        } else {
          // Active task: allow normal move
          next.splice(Math.min(destination.index, next.length), 0, removed);
        }

        const nextByList = { ...(locationOrderByList[selectedListId] || {}) };
        nextByList[srcKey] = clampToPartition(next);
        setLocationOrderByList(prev => ({ ...prev, [selectedListId]: nextByList }));

        // Reorder the underlying tasks array to reflect the new section order
        setTasks(prev => {
          const order = nextByList[srcKey];
          const orderIndex = new Map(order.map((id, i) => [id, i]));
          const belongs = (t) => (((t?.details?.locationNickname) || NO_LOCATION_KEY)) === normalizeLocKey(srcKey);
          const sortedSection = prev.filter(belongs).slice().sort((a, b) => (orderIndex.get(a.id) - orderIndex.get(b.id)));
          let ptr = 0;
          return prev.map(t => belongs(t) ? sortedSection[ptr++] : t);
        });
        return;
      }

      // Cross-section move
      const srcNext = beforeSrc.slice();
      const [removed] = srcNext.splice(source.index, 1);
      const dstNext = beforeDst.slice();

      if (isMovedDone) {
        // Completed task: insert at or after the first completed index in the destination section
        const boundaryDst = firstDoneIdx(dstNext);
        const destIndex = Math.max(destination.index, boundaryDst);
        dstNext.splice(Math.min(destIndex, dstNext.length), 0, removed);
      } else {
        // Active task: insert at requested index (bounded)
        dstNext.splice(Math.min(destination.index, dstNext.length), 0, removed);
      }

      const nextByList = { ...(locationOrderByList[selectedListId] || {}) };
      nextByList[srcKey] = clampToPartition(srcNext);
      nextByList[dstKey] = clampToPartition(dstNext);
      setLocationOrderByList(prev => ({ ...prev, [selectedListId]: nextByList }));

      // Update the task's location and reorder both affected sections in the tasks array
      setTasks(prev => {
        // 1) Update moved task's locationNickname
        let updated = prev.map(t => (
          t.id === removed
            ? { ...t, details: { ...(t.details || {}), locationNickname: (dstKey === NO_LOCATION_KEY ? undefined : dstKey) } }
            : t
        ));

        // 2) Helper to apply a section order onto the flat tasks array without disturbing other sections
        const applySectionOrder = (arr, key, order) => {
          const index = new Map(order.map((id, i) => [id, i]));
          const belongs = (t) => (((t?.details?.locationNickname) || NO_LOCATION_KEY)) === normalizeLocKey(key);
          const sorted = arr.filter(belongs).slice().sort((a, b) => (index.get(a.id) - index.get(b.id)));
          let i = 0;
          return arr.map(t => belongs(t) ? sorted[i++] : t);
        };

        // 3) Apply orders for both sections
        updated = applySectionOrder(updated, srcKey, nextByList[srcKey] || []);
        updated = applySectionOrder(updated, dstKey, nextByList[dstKey] || []);
        return updated;
      });
      return;
    }

    // Default (priority / single droppable): keep completed at bottom globally
    const activeTasks = tasks.filter(task => !task.done);
    const completedTasks = tasks.filter(task => task.done);
    const draggedTask = tasks[source.index];

    if (!draggedTask || draggedTask.done === undefined) return;
    if (!draggedTask.done) {
      const destIdx = Math.min(destination.index, activeTasks.length - 1);
      const reorderedActive = reorder(activeTasks, source.index, destIdx);
      setTasks([...reorderedActive, ...completedTasks]);
    } else {
      const sourceIdx = source.index - activeTasks.length;
      const rawDestIdx = destination.index - activeTasks.length;
      const destIdx = Math.max(0, Math.min(rawDestIdx, completedTasks.length - 1));
      const reorderedCompleted = reorder(completedTasks, sourceIdx, destIdx);
      setTasks([...activeTasks, ...reorderedCompleted]);
    }
  };

  // Track first mount to avoid animating initial load
  const hasMountedRef = useRef(false);
  useEffect(() => { hasMountedRef.current = true; }, []);

  // Keep portal dropdown positioned to its trigger
  useEffect(() => {
    if (!listDropdownOpen) return;
    const updateRect = () => {
      if (!listButtonRef.current) return;
      const r = listButtonRef.current.getBoundingClientRect();
      setListDropdownRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [listDropdownOpen]);

  useEffect(() => {
    if (!projectDropdownOpen) return;
    const updateRect = () => {
      if (!projectButtonRef.current) return;
      const r = projectButtonRef.current.getBoundingClientRect();
      setProjectDropdownRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [projectDropdownOpen]);

  // Keep the location portal dropdown positioned to its trigger
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!locationDropdownOpen) return;
    const updateRect = () => {
      if (!locationButtonRef.current) return;
      const r = locationButtonRef.current.getBoundingClientRect();
      setLocationDropdownRect({
        top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width
      });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [locationDropdownOpen]);

  // Keep the reassign portal dropdown positioned to its trigger (Edit Location confirm delete)
  useEffect(() => {
    if (!reassignDropdownOpen) return;
    const updateRect = () => {
      if (!reassignButtonRef.current) return;
      const r = reassignButtonRef.current.getBoundingClientRect();
      setReassignDropdownRect({
        top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width
      });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [reassignDropdownOpen]);

  // Auto-size the task editing textarea so multi-line tasks remain fully visible
  useEffect(() => {
    if (!editingTaskId || !editingTextareaRef.current) return;
    const el = editingTextareaRef.current;
    const autoResize = () => {
      el.style.boxSizing = 'border-box';
      // lock a stable one-line height based on computed line-height
      const cs = window.getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight || '0');
      if (lh) el.style.minHeight = lh + 'px';
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    };
    // Initial resize after mount/paint
    requestAnimationFrame(autoResize);
    // Also resize on window resize (layout changes)
    window.addEventListener('resize', autoResize);
    return () => window.removeEventListener('resize', autoResize);
  }, [editingTaskId]);

  // Place caret at the end on entering edit mode
  useEffect(() => {
    if (!editingTaskId || !editingTextareaRef.current) return;
    const el = editingTextareaRef.current;
    requestAnimationFrame(() => {
      try {
        const len = el.value?.length ?? 0;
        el.setSelectionRange(len, len);
        el.focus({ preventScroll: true });
      } catch {}
    });
  }, [editingTaskId]);

  // Compute per-line rects for strikethrough when animation starts
  useEffect(() => {
    const id = animatingTask;
    if (!id) return;
    const el = textRefs.current[id];
    if (!el) return;

    const computeRects = () => {
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        const containerRect = el.getBoundingClientRect();
        const rects = Array.from(range.getClientRects()).map(r => ({
          left: Math.max(0, r.left - containerRect.left),
          top: r.top - containerRect.top + r.height / 2,
          width: r.width,
          height: r.height
        }));
        setStrikeRectsByTask(prev => ({ ...prev, [id]: rects }));
      } catch {}
    };
    computeRects();
    window.addEventListener('resize', computeRects);
    return () => window.removeEventListener('resize', computeRects);
  }, [animatingTask]);

  // Measure whether the link pill wrapped to the next line (so we can realign it flush-left)
  useEffect(() => {
    // Skip measurement on non-task views, and while *any* overlay/menu is open to avoid feedback loops
    const overlayOpen = (
      openDialogId !== null ||
      listDropdownOpen ||
      locationDropdownOpen ||
      showAddListModal ||
      showDeleteListModal ||
      showRecoverModal ||
      showDeleteAllDeletedModal ||
      headerMenuOpen
    );
    if (selectedListId == null || selectedListId === RECENTLY_DELETED_ID || overlayOpen) {
      // Ensure mapping is cleared once when leaving a list view or while overlay UI is open
      setLinkWrappedByTask(prev => (Object.keys(prev).length ? {} : prev));
      return;
    }

    const measureWrap = () => {
      // Build the next mapping in a local object first
      const next = {};
      const currentTasks = hideCompleted ? tasks.filter(t => !t.done) : tasks;

      currentTasks.forEach(t => {
        const linkEl = linkRefs.current[t.id];
        const textEl = textRefs.current[t.id];
        if (!linkEl || !textEl) return;

        // Determine the last line's top for the task text using precise rects
        let lastLineTop;
        try {
          const range = document.createRange();
          range.selectNodeContents(textEl);
          const rects = Array.from(range.getClientRects());
          if (rects.length) {
            lastLineTop = Math.max(...rects.map(r => r.top));
          } else {
            lastLineTop = textEl.getBoundingClientRect().top;
          }
        } catch {
          lastLineTop = textEl.getBoundingClientRect().top;
        }

        const linkTop = linkEl.getBoundingClientRect().top;
        const wrapped = (linkTop - lastLineTop) > 1; // epsilon to avoid jitter
        next[t.id] = wrapped;
      });

      // Only update state if the mapping actually changed
      setLinkWrappedByTask(prev => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length !== nextKeys.length) return next;
        for (const k of nextKeys) {
          if (prev[k] !== next[k]) return next;
        }
        return prev; // no change -> avoid causing a re-render
      });
    };

    // Initial measure after paint
    const raf = requestAnimationFrame(measureWrap);

    // Recompute on resize and when fonts are ready (layout changes)
    window.addEventListener('resize', measureWrap);
    if (document && 'fonts' in document) {
      try { document.fonts.ready.then(measureWrap); } catch {}
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measureWrap);
    };
  }, [
    selectedListId,
    tasks,
    hideCompleted,
    openDialogId,
    listDropdownOpen,
    projectDropdownOpen,
    locationDropdownOpen,
    showAddListModal,
    showDeleteListModal,
    showRecoverModal,
    showDeleteAllDeletedModal,
    headerMenuOpen
  ]);

  // Ensure selected item is visible when opening the dropdown
  useEffect(() => {
    if (!listDropdownOpen || !listMenuRef.current) return;
    // wait for paint
    requestAnimationFrame(() => {
      const selectedEl = listMenuRef.current.querySelector('[data-selected="true"]');
      if (selectedEl && typeof selectedEl.scrollIntoView === 'function') {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    });
  }, [listDropdownOpen]);

  // dnd-kit sensors (must be declared at top-level, not inline)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Slide transition tuning (faster on desktop, slightly slower on touch)
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const slideTransition = prefersReducedMotion
    ? { duration: 0, ease: 'linear' }
    : { duration: 0.30, ease: [0.22, 0.61, 0.36, 1] };
  const modalTransition = slideTransition;
  const modalDurationMs = prefersReducedMotion ? 0 : 300;

  const openDueDatePicker = () => {
    const input = dueDateInputRef.current;
    if (!input) return;
    input.focus({ preventScroll: true });
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
      } catch {}
    }
  };


  // Shared Task Details Modal (single instance) — original UI preserved; only Location is upgraded
  const renderSharedTaskDetailsModal = () => {
    const isOpen = openDialogId != null;
    const task = isOpen ? tasks.find(t => t.id === openDialogId) : null;

    // Remember the last opened task so content remains while animating out
    if (task) lastDialogTaskRef.current = task;
    const displayTask = lastDialogTaskRef.current;

    // If we've never opened, don't render anything
    if (!isOpen && !displayTask) return null;

    const currentListTitle =
      (lists.find(l => l.id === (selectedListForDialog ?? selectedListId))?.title) ||
      "Select a list";
    const displayTaskChildren = displayTask ? getProjectChildren(displayTask.id) : [];
    const displayTaskIsChild = !!getProjectParentId(displayTask);
    const projectChoices = tasks.filter(t => t.id !== displayTask?.id && !getProjectParentId(t));
    const dueDateCalendarMonthValue = dueDateCalendarMonth || getDueDateCalendarMonth(displayTask?.details?.dueDate);
    const dueDateCalendarMonthDate = parseDateInputValue(dueDateCalendarMonthValue) || new Date();
    const dueDateCalendarMonthLabel = new Intl.DateTimeFormat(undefined, {
      month: 'long',
      year: 'numeric'
    }).format(dueDateCalendarMonthDate);
    const dueDateCalendarDays = getDueDateCalendarDays(dueDateCalendarMonthValue, displayTask?.details?.dueDate);

    return (
      <>
      <Modal
        isOpen={isOpen}
        onRequestClose={() => {
          setOpenDialogId(null);
          setListDropdownOpen(false);
          setLocationDropdownOpen(false);
          setProjectDropdownOpen(false);
          setDueDateCalendarOpen(false);
        }}
        className="bg-[color:var(--app-surface)] rounded-2xl w-full max-w-md mx-auto border-none shadow-2xl relative outline-none flex flex-col max-h-[85vh] overflow-hidden"
        overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
        closeTimeoutMS={modalDurationMs}
        contentElement={(props, children) => (
          <motion.div
            {...props}
            initial={{ y: '100vh' }}
            animate={isOpen ? { y: 0 } : { y: '100vh' }}
            transition={modalTransition}
            style={{ willChange: 'transform' }}
          >
            {children}
          </motion.div>
        )}
      >
        {/* Only render the inner content when a task is selected; keep Modal mounted for exit animation */}
          <>
            <div className="shrink-0 h-10 px-4 sm:px-6 pt-2 flex items-start justify-end bg-[color:var(--app-surface)]">
              <button
                className="h-8 w-8 rounded-full inline-flex items-center justify-center text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]"
                onClick={() => {
                  setOpenDialogId(null);
                  setListDropdownOpen(false);
                  setLocationDropdownOpen(false);
                  setProjectDropdownOpen(false);
                  setDueDateCalendarOpen(false);
                }}
                aria-label="Close"
                type="button"
              >
                <X size={22} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="px-6 sm:px-8 overflow-y-auto flex-1 pb-5 sm:pb-6">
              {taskDetailsView === 'subtasks' ? (
                <div className="pt-2 sm:pt-3 space-y-4">
                  <button
                    type="button"
                    onClick={() => setTaskDetailsView('details')}
                    className="inline-flex items-center gap-1 text-[color:var(--app-text-secondary)] hover:text-[color:var(--app-text)] font-medium"
                  >
                    <ChevronLeft size={18} strokeWidth={2.5} />
                    Details
                  </button>
                  <h2 className="text-[color:var(--app-text)] text-xl font-bold">Subtasks</h2>
                  <div className="space-y-3">
                    {displayTaskChildren.length === 0 ? (
                      <p className="text-[color:var(--app-text-muted)]">No subtasks yet.</p>
                    ) : displayTaskChildren.map(child => (
                      <div key={child.id} className="flex items-center bg-[color:var(--app-input-bg)] rounded-2xl pl-[12px] pr-3 py-2 min-h-[48px]">
                        <button
                          type="button"
                          onClick={() => handleCheck(child.id)}
                          className="w-6 h-6 shrink-0 border-2 border-[color:var(--app-text)] rounded-full flex items-center justify-center mr-[12px]"
                          aria-label={child.done ? 'Mark incomplete' : 'Mark complete'}
                        >
                          {child.done && <div className="w-3 h-3 bg-[color:var(--app-text)] rounded-full" />}
                        </button>
                        <input
                          value={child.text}
                          onChange={e => updateTask(child.id, 'text', e.target.value)}
                          className={`flex-1 min-w-0 bg-transparent text-[color:var(--app-text)] focus:outline-none focus:ring-0 ${
                            child.done ? 'text-[color:var(--app-text-muted)] opacity-50 line-through' : ''
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                  <form
                    className="flex items-center pt-2"
                    onSubmit={event => {
                      event.preventDefault();
                      addSubtask(displayTask.id);
                    }}
                  >
                    <input
                      value={subtaskInput}
                      onChange={e => setSubtaskInput(e.target.value)}
                      placeholder="Add task"
                      className="placeholder-[color:var(--app-text-muted)] text-base flex-1 bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-l-2xl px-4 h-12 focus:outline-none focus:ring-0"
                    />
                    <button
                      type="submit"
                      className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] rounded-r-2xl h-12 px-5 font-semibold"
                    >
                      Add task
                    </button>
                  </form>
                </div>
              ) : (
              <form className="space-y-4 sm:space-y-5">
                {/* Title (original) */}
                <div className="pt-2 sm:pt-3">
                  <label htmlFor={`task-title-${displayTask.id}`} className="flex items-center gap-2 mb-1.5 text-[color:var(--app-text)] font-medium">
                    <Pen size={18} />
                    Title
                  </label>
                  <input
                    id={`task-title-${displayTask.id}`}
                    name={`task-title-${displayTask.id}`}
                    placeholder="Title"
                    value={displayTask.text}
                    onChange={e => updateTask(displayTask.id, 'text', e.target.value)}
                    className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
                    type="text"
                  />
                </div>

                {/* Description (original) */}
                <div>
                  <label htmlFor={`task-desc-${displayTask.id}`} className="flex items-center gap-2 mb-1.5 text-[color:var(--app-text)] font-medium">
                    <Info size={19} />
                    Description
                  </label>
                  <textarea
                    id={`task-desc-${displayTask.id}`}
                    name={`task-desc-${displayTask.id}`}
                    placeholder="Add more details..."
                    value={displayTask.details.description || ""}
                    onChange={e => updateTask(displayTask.id, 'details', { ...displayTask.details, description: e.target.value })}
                    className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 resize-none border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
                    rows={3}
                  />
                </div>

                {/* Location (UPGRADED) */}
                <div>
                  <label htmlFor={`task-location-${displayTask.id}`} className="flex items-center gap-2 mb-1.5 text-[color:var(--app-text)] font-medium">
                    <MapPin size={18} />
                    Location
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      ref={locationButtonRef}
                      onClick={() => {
                        if (!locationDropdownOpen && locationButtonRef.current) {
                          const r = locationButtonRef.current.getBoundingClientRect();
                          setLocationDropdownRect({
                            top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width
                          });
                        }
                        setLocationDropdownOpen(prev => !prev);
                      }}
                      className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl pl-3.5 pr-11 py-2 sm:pl-4 sm:pr-12 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm text-left"
                      aria-haspopup="listbox"
                      aria-expanded={locationDropdownOpen}
                      aria-labelledby={`task-location-${displayTask.id}`}
                    >
                      <span className="truncate">{displayTask?.details?.locationNickname || 'No location'}</span>
                    </button>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                      <div className="w-[1px] h-6 bg-[color:var(--app-border)] mr-4" />
                      <ChevronDown className="text-[color:var(--app-text-secondary)] font-bold" size={18} />
                    </div>

                    {locationDropdownOpen && locationDropdownRect && createPortal(
                      <>
                        <div className="fixed inset-0 z-[60]" onClick={() => setLocationDropdownOpen(false)} />
                        <div
                          ref={locationMenuRef}
                          className="fixed z-[70] bg-[color:var(--app-input-bg)] dark:bg-[color:var(--app-surface)] border border-[color:var(--app-border)] rounded-2xl shadow-2xl p-2 overflow-auto space-y-1"
                          style={{
                            top: Math.round(locationDropdownRect.bottom + 8),
                            left: Math.round(locationDropdownRect.left),
                            width: Math.round(locationDropdownRect.width),
                            maxHeight: Math.min(285, window.innerHeight - locationDropdownRect.bottom - 24)
                          }}
                          role="listbox"
                        >
                          <button
                            type="button"
                            className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                              !displayTask?.details?.locationNickname
                                ? 'bg-[#f5f5f5] dark:bg-[color:var(--app-surface-muted)]'
                                : 'hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]'
                            }`}
                            onClick={() => {
                              updateTask(displayTask.id, 'details', { ...(displayTask.details || {}), locationNickname: undefined });
                              setLocationDropdownOpen(false);
                            }}
                          >
                            No location
                          </button>
                          {locations.slice().sort((a, b) => (a.nickname || '').toLowerCase().localeCompare((b.nickname || '').toLowerCase())).map(loc => (
                            <button
                              key={loc.nickname}
                              type="button"
                              className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                                displayTask?.details?.locationNickname === loc.nickname
                                  ? 'bg-[#f5f5f5] dark:bg-[color:var(--app-surface-muted)]'
                                  : 'hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]'
                              }`}
                              onClick={() => {
                                updateTask(displayTask.id, 'details', { ...(displayTask.details || {}), locationNickname: loc.nickname });
                                setLocationDropdownOpen(false);
                              }}
                            >
                              <div className="flex flex-col">
                                <span>{loc.nickname}</span>
                                {loc.address ? <span className="text-xs text-[color:var(--app-text-muted)]">{loc.address}</span> : null}
                              </div>
                            </button>
                          ))}
                          <div className="h-px bg-[color:var(--app-border)] my-2" />
                          <button
                            type="button"
                            className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]"
                            onClick={() => {
                              setLocationDropdownOpen(false);
                              setNewLocNick("");
                              setNewLocAddr("");
                              setNewLocError("");
                              setShowAddLocationSheet(true);
                            }}
                          >
                            Add New Location…
                          </button>
                          <div className="h-px bg-[color:var(--app-border)] my-2" />
                          <button
                            type="button"
                            className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]"
                            onClick={() => {
                              setLocationDropdownOpen(false);
                              setShowManageLocationsSheet(true);
                            }}
                          >
                            Manage My Locations
                          </button>
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                </div>

                {displayTaskIsChild && (
                  <div>
                    <div className="flex items-center gap-2 mb-1.5 text-[color:var(--app-text)] font-medium" id={`task-project-label-${displayTask.id}`}>
                      <ListIcon size={18} />
                      Project
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        ref={projectButtonRef}
                        onClick={() => {
                          if (!projectDropdownOpen && projectButtonRef.current) {
                            const r = projectButtonRef.current.getBoundingClientRect();
                            setProjectDropdownRect({
                              top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width
                            });
                          }
                          setProjectDropdownOpen(prev => !prev);
                        }}
                        className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl pl-3.5 pr-11 py-2 sm:pl-4 sm:pr-12 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm text-left"
                        aria-haspopup="listbox"
                        aria-expanded={projectDropdownOpen}
                        aria-labelledby={`task-project-label-${displayTask.id}`}
                      >
                        <span className="truncate">
                          {projectChoices.find(projectTask => projectTask.id === displayTask.details?.projectTaskId)?.text || 'None'}
                        </span>
                      </button>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                        <div className="w-[1px] h-6 bg-[color:var(--app-border)] mr-4" />
                        <ChevronDown className="text-[color:var(--app-text-secondary)] font-bold" size={18} />
                      </div>

                      {projectDropdownOpen && projectDropdownRect && createPortal(
                        <>
                          <div className="fixed inset-0 z-[60]" onClick={() => setProjectDropdownOpen(false)} />
                          <div
                            ref={projectMenuRef}
                            className="fixed z-[70] bg-[color:var(--app-input-bg)] dark:bg-[color:var(--app-surface)] border border-[color:var(--app-border)] rounded-2xl shadow-2xl p-2 overflow-auto space-y-1"
                            style={{
                              top: Math.round(projectDropdownRect.bottom + 8),
                              left: Math.round(projectDropdownRect.left),
                              width: Math.round(projectDropdownRect.width),
                              maxHeight: Math.min(240, window.innerHeight - projectDropdownRect.bottom - 24)
                            }}
                            role="listbox"
                          >
                            <button
                              type="button"
                              className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                                !displayTask.details?.projectTaskId
                                  ? 'bg-[#f5f5f5] dark:bg-[color:var(--app-surface-muted)]'
                                  : 'hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]'
                              }`}
                              onClick={() => {
                                assignTaskToProject(displayTask.id, null);
                                setProjectDropdownOpen(false);
                              }}
                            >
                              None
                            </button>
                            {projectChoices.map(projectTask => (
                              <button
                                key={projectTask.id}
                                type="button"
                                className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                                  displayTask.details?.projectTaskId === projectTask.id
                                    ? 'bg-[#f5f5f5] dark:bg-[color:var(--app-surface-muted)]'
                                    : 'hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]'
                                }`}
                                onClick={() => {
                                  assignTaskToProject(displayTask.id, projectTask.id);
                                  setProjectDropdownOpen(false);
                                }}
                              >
                                {projectTask.text || 'Untitled'}
                              </button>
                            ))}
                          </div>
                        </>,
                        document.body
                      )}
                    </div>
                  </div>
                )}

                {/* List selector (original) */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5 text-[color:var(--app-text)] font-medium" id={`task-list-label-${displayTask.id}`}>
                    <ListIcon size={18} />
                    List
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      ref={listButtonRef}
                      onClick={() => {
                        if (!listDropdownOpen && listButtonRef.current) {
                          const r = listButtonRef.current.getBoundingClientRect();
                          setListDropdownRect({
                            top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width
                          });
                        }
                        setListDropdownOpen(prev => !prev);
                      }}
                      className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl pl-3.5 pr-11 py-2 sm:pl-4 sm:pr-12 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm text-left"
                      aria-labelledby={`task-list-label-${displayTask.id}`}
                    >
                      <span className="truncate">{currentListTitle}</span>
                    </button>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                      <div className="w-[1px] h-6 bg-[color:var(--app-border)] mr-4" />
                      <ChevronDown className="text-[color:var(--app-text-secondary)] font-bold" size={18} />
                    </div>

                    {listDropdownOpen && listDropdownRect && createPortal(
                      <>
                        <div className="fixed inset-0 z-[60]" onClick={() => setListDropdownOpen(false)} />
                        <div
                          ref={listMenuRef}
                          className="fixed z-[70] bg-[color:var(--app-input-bg)] dark:bg-[color:var(--app-surface)] border border-[color:var(--app-border)] rounded-2xl shadow-2xl p-2 overflow-auto space-y-1"
                          style={{
                            top: Math.round(listDropdownRect.bottom + 8),
                            left: Math.round(listDropdownRect.left),
                            width: Math.round(listDropdownRect.width),
                            maxHeight: Math.min(188, window.innerHeight - listDropdownRect.bottom - 24)
                          }}
                        >
                          {lists.map(l => (
                            <button
                              key={l.id}
                              type="button"
                              data-selected={l.id === (selectedListForDialog ?? selectedListId)}
                              className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                                l.id === (selectedListForDialog ?? selectedListId)
                                  ? 'bg-[#f5f5f5] dark:bg-[color:var(--app-surface-muted)]'
                                  : 'hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] dark:hover:bg-[color:var(--app-input-bg)] dark:focus:bg-[color:var(--app-input-bg)]'
                              }`}
                              onClick={() => { setSelectedListForDialog(l.id); setListDropdownOpen(false); }}
                            >
                              {l.title}
                            </button>
                          ))}
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                </div>

                {!displayTaskIsChild && (
                  <button
                    type="button"
                    onClick={() => setTaskDetailsView('subtasks')}
                    className="relative w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl pl-3.5 pr-11 py-2 sm:pl-4 sm:pr-12 sm:py-2.5 border border-[color:var(--app-border)] transition text-base shadow-sm text-left hover:bg-[color:var(--app-surface-hover)]"
                  >
                    <span>Subtasks</span>
                    <ChevronLeft size={18} className="absolute right-4 top-1/2 -translate-y-1/2 rotate-180 text-[color:var(--app-text-secondary)]" />
                  </button>
                )}


                <div>
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <label id={`task-due-date-label-${displayTask.id}`} htmlFor={`task-due-date-${displayTask.id}`} className="flex items-center gap-2 text-[color:var(--app-text)] font-medium">
                      <CalendarDays size={18} />
                      Due date
                    </label>
                    {displayTask.details?.dueDate ? (
                      <button
                        type="button"
                        onClick={() => {
                          updateTask(displayTask.id, 'details', { ...(displayTask.details || {}), dueDate: undefined });
                          setDueDateCalendarOpen(false);
                        }}
                        className="text-sm font-medium text-[color:var(--app-text-secondary)] hover:text-[color:var(--app-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] rounded-lg px-1"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  {canHover ? (
                    <div className="relative">
                      <button
                        type="button"
                        id={`task-due-date-${displayTask.id}`}
                        aria-labelledby={`task-due-date-label-${displayTask.id} task-due-date-value-${displayTask.id}`}
                        aria-haspopup="dialog"
                        aria-expanded={dueDateCalendarOpen}
                        onClick={() => {
                          setDueDateCalendarMonth(getDueDateCalendarMonth(displayTask.details?.dueDate));
                          setDueDateCalendarOpen(open => !open);
                        }}
                        className="relative w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl pl-3.5 pr-10 py-2 sm:pl-4 sm:py-2.5 border border-[color:var(--app-border)] transition text-base shadow-sm text-left hover:bg-[color:var(--app-surface-hover)] focus:outline-none focus:ring-2 focus:ring-[color:var(--app-ring)]"
                      >
                        <span
                          id={`task-due-date-value-${displayTask.id}`}
                          className={displayTask.details?.dueDate ? 'text-[color:var(--app-text)]' : 'text-[color:var(--app-text-muted)]'}
                        >
                          {displayTask.details?.dueDate ? getDueDateFieldLabel(displayTask.details.dueDate) : 'mm/dd/yyyy'}
                        </span>
                        <CalendarDays size={17} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[color:var(--app-text-secondary)]" />
                      </button>
                      {dueDateCalendarOpen && (
                        <div
                          role="dialog"
                          aria-label="Choose due date"
                          className="absolute left-0 right-0 bottom-[calc(100%+0.5rem)] z-[80] rounded-2xl border border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] p-3 shadow-2xl"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <button
                              type="button"
                              aria-label="Previous month"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full text-[color:var(--app-text-secondary)] hover:bg-[color:var(--app-surface-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]"
                              onClick={() => setDueDateCalendarMonth(shiftDueDateCalendarMonth(dueDateCalendarMonthValue, -1))}
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <div className="text-sm font-semibold text-[color:var(--app-text)]">{dueDateCalendarMonthLabel}</div>
                            <button
                              type="button"
                              aria-label="Next month"
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full text-[color:var(--app-text-secondary)] hover:bg-[color:var(--app-surface-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]"
                              onClick={() => setDueDateCalendarMonth(shiftDueDateCalendarMonth(dueDateCalendarMonthValue, 1))}
                            >
                              <ChevronLeft size={18} className="rotate-180" />
                            </button>
                          </div>
                          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wide text-[color:var(--app-text-muted)] mb-1">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                              <div key={day}>{day}</div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {dueDateCalendarDays.map(day => (
                              <button
                                key={day.value}
                                type="button"
                                onClick={() => {
                                  updateTask(displayTask.id, 'details', { ...(displayTask.details || {}), dueDate: day.value });
                                  setDueDateCalendarMonth(getDueDateCalendarMonth(day.value));
                                  setDueDateCalendarOpen(false);
                                }}
                                className={`h-8 rounded-full text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] ${
                                  day.isSelected
                                    ? 'bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] font-semibold'
                                    : day.isToday
                                      ? 'border border-[color:var(--app-border-strong)] text-[color:var(--app-text)]'
                                      : day.inCurrentMonth
                                        ? 'text-[color:var(--app-text)] hover:bg-[color:var(--app-surface-hover)]'
                                        : 'text-[color:var(--app-text-muted)] opacity-45 hover:bg-[color:var(--app-surface-hover)]'
                                }`}
                              >
                                {day.day}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="relative w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] transition text-base shadow-sm"
                      onClick={openDueDatePicker}
                    >
                      <span
                        aria-hidden="true"
                        className={`block leading-[1.5] ${
                          displayTask.details?.dueDate ? 'text-[color:var(--app-text)]' : 'text-[color:var(--app-text-muted)]'
                        }`}
                      >
                        {displayTask.details?.dueDate ? getDueDateFieldLabel(displayTask.details.dueDate) : 'mm/dd/yyyy'}
                      </span>
                      <input
                        ref={dueDateInputRef}
                        id={`task-due-date-${displayTask.id}`}
                        name={`task-due-date-${displayTask.id}`}
                        value={displayTask.details?.dueDate || ""}
                        onChange={e => updateTask(displayTask.id, 'details', { ...(displayTask.details || {}), dueDate: e.target.value || undefined })}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        type="date"
                      />
                    </div>
                  )}
                </div>

                {/* URL (original) — moved to be the last field */}
                <div>
                  <label htmlFor={`task-url-${displayTask.id}`} className="flex items-center gap-2 mb-1.5 text-[color:var(--app-text)] font-medium">
                    <Link size={18} />
                    URL
                  </label>
                  <input
                    placeholder="example.com"
                    value={displayTask.details.link || ""}
                    onChange={e => updateTask(displayTask.id, 'details', { ...displayTask.details, link: e.target.value })}
                    onBlur={e => {
                      const normalized = normalizeUrl(e.target.value);
                      if (normalized && normalized !== e.target.value) {
                        updateTask(displayTask.id, 'details', { ...displayTask.details, link: normalized });
                      }
                    }}
                    className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
                    type="url"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="url"
                    enterKeyHint="done"
                    name={`task-url-${displayTask.id}`}
                    id={`task-url-${displayTask.id}`}
                  />
                </div>
              </form>
              )}
            </div>

            {/* Sticky footer (original) */}
            {taskDetailsView !== 'subtasks' && (
            <div className="sticky bottom-0 px-6 sm:px-8 pt-3 sm:pt-4 pb-4 sm:pb-5 bg-[color:var(--app-surface)]/95 backdrop-blur rounded-b-2xl z-10">
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => {
                    deleteTask(displayTask.id);
                    setListDropdownOpen(false);
                    setLocationDropdownOpen(false);
                    setProjectDropdownOpen(false);
                  }}
                  className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const targetListId = selectedListForDialog ?? selectedListId;
                    if (targetListId && targetListId !== selectedListId) {
                      const taskToMove = tasks.find(t => t.id === displayTask.id);
                      if (taskToMove) {
                        setLists(prev => {
                          const sourceIdx = prev.findIndex(l => l.id === selectedListId);
                          const destIdx = prev.findIndex(l => l.id === targetListId);
                          if (sourceIdx === -1 || destIdx === -1) return prev;
                          const newSourceTasks = (prev[sourceIdx].tasks || [])
                            .filter(t => t.id !== displayTask.id)
                            .map(t => t?.details?.projectTaskId === displayTask.id
                              ? { ...t, details: { ...(t.details || {}), projectTaskId: undefined } }
                              : t
                            );
                          const { projectTaskId, ...moveDetails } = taskToMove.details || {};
                          const newDestTasks = [{ ...taskToMove, details: moveDetails }, ...(prev[destIdx].tasks || [])];
                          return prev.map((l, i) => {
                            if (i === sourceIdx) return { ...l, tasks: newSourceTasks };
                            if (i === destIdx) return { ...l, tasks: newDestTasks };
                            return l;
                          });
                        });
                        setTasks(prev => prev
                          .filter(t => t.id !== displayTask.id)
                          .map(t => t?.details?.projectTaskId === displayTask.id
                            ? { ...t, details: { ...(t.details || {}), projectTaskId: undefined } }
                            : t
                          )
                        );
                        // Clean location ordering for this task in the current list
                        setLocationOrderByList(prev => {
                          const byList = { ...(prev[selectedListId] || {}) };
                          let changed = false;
                          Object.keys(byList).forEach(k => {
                            const arr = Array.isArray(byList[k]) ? byList[k].filter(tid => tid !== displayTask.id) : byList[k];
                            if (arr !== byList[k]) { byList[k] = arr; changed = true; }
                          });
                          if (!changed) return prev;
                          return { ...prev, [selectedListId]: byList };
                        });
                        setProjectTopOrderByList(prev => ({
                          ...prev,
                          [selectedListId]: (prev[selectedListId] || []).filter(tid => tid !== displayTask.id)
                        }));
                        setProjectChildOrderByList(prev => {
                          const byList = { ...(prev[selectedListId] || {}) };
                          delete byList[displayTask.id];
                          Object.keys(byList).forEach(k => {
                            byList[k] = (byList[k] || []).filter(tid => tid !== displayTask.id);
                          });
                          return { ...prev, [selectedListId]: byList };
                        });
                      }
                    }
                    setOpenDialogId(null);
                    setListDropdownOpen(false);
                    setLocationDropdownOpen(false);
                    setProjectDropdownOpen(false);
                    setDueDateCalendarOpen(false);
                  }}
                  className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
                >
                  Done
                </button>
              </div>
            </div>
            )}
          </>
      </Modal>
      <Modal
        isOpen={showAddLocationSheet}
        onRequestClose={() => {
          setShowAddLocationSheet(false);
          setNewLocError("");
        }}
        className="bg-[#f5f5f5] dark:bg-[color:var(--app-surface)] rounded-2xl w-full max-w-md mx-auto border-none shadow-2xl relative outline-none flex flex-col"
        overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
        closeTimeoutMS={modalDurationMs}
        contentElement={(props, children) => (
          <motion.div
            {...props}
            initial={{ y: '100vh' }}
            animate={showAddLocationSheet ? { y: 0 } : { y: '100vh' }}
            transition={modalTransition}
            style={{ willChange: 'transform' }}
          >
            {children}
          </motion.div>
        )}
      >
        <button
          className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
          onClick={() => { setShowAddLocationSheet(false); setNewLocError(""); }}
          aria-label="Close"
          type="button"
        >
          <X size={22} />
        </button>
        <div className="px-6 sm:px-8 pt-9 pb-5 sm:pb-6">
          <h2 className="text-[color:var(--app-text)] font-bold text-lg mb-4">Add Location</h2>
          <div className="space-y-3">
            <input
              placeholder="Nickname (e.g., Costco)"
              value={newLocNick}
              onChange={e => { setNewLocNick(e.target.value); setNewLocError(""); }}
              className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
              type="text"
              inputMode="text"
              autoCapitalize="words"
              autoCorrect="off"
              autoFocus
            />
            <input
              placeholder="Address (optional)"
              value={newLocAddr}
              onChange={e => setNewLocAddr(e.target.value)}
              className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
              type="text"
              inputMode="text"
              autoCapitalize="words"
              autoCorrect="off"
            />
            {newLocError ? <div className="text-red-400 text-sm">{newLocError}</div> : null}
          </div>
        </div>
        <div className="px-6 sm:px-8 pb-4 sm:pb-5">
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => { setShowAddLocationSheet(false); setNewLocError(""); }}
              className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const nickname = (newLocNick || "").trim();
                if (!nickname) { setNewLocError('Nickname is required'); return; }
                const dup = locations.some(l => (l.nickname || '').toLowerCase() === nickname.toLowerCase());
                if (dup) { setNewLocError('Nickname already exists'); return; }
                const address = (newLocAddr || '').trim();
                setLocations(prev => [...prev, { nickname, address }]);
                // Ensure the currently open/last task gets this location selected
                const taskId = (openDialogId != null ? openDialogId : lastDialogTaskRef.current?.id);
                if (taskId) {
                  const task = tasks.find(t => t.id === taskId) || lastDialogTaskRef.current;
                  if (task) updateTask(task.id, 'details', { ...(task.details || {}), locationNickname: nickname });
                }
                setShowAddLocationSheet(false);
                setNewLocNick("");
                setNewLocAddr("");
                setNewLocError("");
              }}
              className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showManageLocationsSheet}
        onRequestClose={() => setShowManageLocationsSheet(false)}
        className="bg-[color:var(--app-surface)] rounded-2xl w-full max-w-md mx-auto border-none shadow-2xl relative outline-none flex flex-col max-h-[calc(100dvh-1rem)] overflow-hidden"
        overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
        closeTimeoutMS={modalDurationMs}
        contentElement={(props, children) => (
          <motion.div
            {...props}
            initial={{ y: '100vh' }}
            animate={showManageLocationsSheet ? { y: 0 } : { y: '100vh' }}
            transition={modalTransition}
            style={{ willChange: 'transform' }}
          >
            {children}
          </motion.div>
        )}
      >
        <button
          className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
          onClick={() => setShowManageLocationsSheet(false)}
          aria-label="Close"
          type="button"
        >
          <X size={22} />
        </button>
        <div className="px-6 sm:px-8 pt-9 pb-3 flex-1 min-h-0 flex flex-col">
          <h2 className="text-[color:var(--app-text)] font-bold text-lg mb-4 shrink-0">My Locations</h2>
          <div
            className="space-y-2 flex-1 overflow-y-auto min-h-0 pr-1 pb-2"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
          >
            {locations.length === 0 ? (
              <div className="text-[color:var(--app-text-muted)] text-sm">No locations yet.</div>
            ) : (
              locations
                .slice()
                .sort((a, b) => (a.nickname || '').toLowerCase().localeCompare((b.nickname || '').toLowerCase()))
                .map(loc => (
                  <div key={loc.nickname} className="flex items-start justify-between bg-[color:var(--app-surface)] rounded-2xl border border-[color:var(--app-border)] px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-[color:var(--app-text)] font-medium truncate">{loc.nickname}</div>
                      {loc.address ? <div className="text-[color:var(--app-text-muted)] text-xs truncate">{loc.address}</div> : null}
                    </div>
                    <div className="ml-3 shrink-0">
                      <button
                        type="button"
                        className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-3 py-1.5 rounded-xl text-sm font-medium hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)] transition"
                        onClick={() => {
                          setEditLocNickOriginal(loc.nickname);
                          setEditLocNick(loc.nickname);
                          setEditLocAddr(loc.address || "");
                          setEditLocError("");
                          setConfirmingDeleteLoc(false);
                          setDeleteReassignTo(NO_LOCATION_KEY);
                          setShowManageLocationsSheet(false);
                          setShowEditLocationSheet(true);
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
        <div className="px-6 sm:px-8 pt-3 pb-5 shrink-0 bg-[color:var(--app-surface)] flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => { setShowManageLocationsSheet(false); setShowAddLocationSheet(true); }}
            className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
          >
            Add a Location
          </button>
          <button
            type="button"
            onClick={() => setShowManageLocationsSheet(false)}
            className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-6 py-2 rounded-2xl font-bold transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
          >
            Done
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={showEditLocationSheet}
        onRequestClose={closeEditLocationToManage}
        className="bg-[color:var(--app-surface)] rounded-2xl w-full max-w-md mx-auto border-none shadow-2xl relative outline-none flex flex-col"
        overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
        closeTimeoutMS={modalDurationMs}
        contentElement={(props, children) => (
          <motion.div
            {...props}
            initial={{ y: '100vh' }}
            animate={showEditLocationSheet ? { y: 0 } : { y: '100vh' }}
            transition={modalTransition}
            style={{ willChange: 'transform' }}
          >
            {children}
          </motion.div>
        )}
      >
        <button
          className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
          onClick={closeEditLocationToManage}
          aria-label="Close"
          type="button"
        >
          <X size={22} />
        </button>
        <div className="px-6 sm:px-8 pt-9 pb-5 sm:pb-6">
          <h2 className="text-[color:var(--app-text)] font-bold text-lg mb-4">Edit Location</h2>
          <div className="space-y-3">
            <input
              placeholder="Nickname"
              value={editLocNick}
              onChange={e => { setEditLocNick(e.target.value); setEditLocError(""); }}
              className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
              type="text"
              inputMode="text"
              autoCapitalize="words"
              autoCorrect="off"
              autoFocus
            />
            <input
              placeholder="Address (optional)"
              value={editLocAddr}
              onChange={e => setEditLocAddr(e.target.value)}
              className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
              type="text"
              inputMode="text"
              autoCapitalize="words"
              autoCorrect="off"
            />
            {editLocError ? <div className="text-red-400 text-sm">{editLocError}</div> : null}
          </div>
        </div>
        <div className="px-6 sm:px-8 pb-4 sm:pb-5 space-y-3">
          {!confirmingDeleteLoc ? (
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  if (!isLocationInUse(editLocNickOriginal)) {
                    cascadeDeleteLocation(editLocNickOriginal, NO_LOCATION_KEY);
                    closeEditLocationToManage();
                  } else {
                    setConfirmingDeleteLoc(true);
                  }
                }}
                className="flex items-center gap-1 text-red-400 underline text-sm hover:text-red-300 transition"
              >
                <Trash2 size={14} />
                Delete
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeEditLocationToManage}
                  className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const oldNick = (editLocNickOriginal || '').trim();
                    const newNick = (editLocNick || '').trim();
                    if (!newNick) { setEditLocError('Nickname is required'); return; }
                    if (newNick.toLowerCase() !== oldNick.toLowerCase() && locations.some(l => (l.nickname || '').toLowerCase() === newNick.toLowerCase())) {
                      setEditLocError('Nickname already exists'); return;
                    }
                    cascadeRenameLocation(oldNick, newNick, (editLocAddr || '').trim());
                    closeEditLocationToManage();
                  }}
                  className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-[color:var(--app-text-secondary)] mb-3">This location is used by one or more tasks. Reassign those tasks to:</div>
              <div className="space-y-2">
                <div className="relative">
                  <button
                    type="button"
                    ref={reassignButtonRef}
                    onClick={() => {
                      if (!reassignDropdownOpen && reassignButtonRef.current) {
                        const r = reassignButtonRef.current.getBoundingClientRect();
                        setReassignDropdownRect({
                          top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width
                        });
                      }
                      setReassignDropdownOpen(prev => !prev);
                    }}
                    className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl pl-3.5 pr-11 py-2 sm:pl-4 sm:pr-12 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm text-left"
                    aria-haspopup="listbox"
                    aria-expanded={reassignDropdownOpen}
                  >
                    <span className="truncate">{deleteReassignTo === NO_LOCATION_KEY ? 'No location' : deleteReassignTo}</span>
                  </button>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                    <div className="w-[1px] h-6 bg-[color:var(--app-border)] mr-4" />
                    <ChevronDown className="text-[color:var(--app-text-secondary)] font-bold" size={18} />
                  </div>

                  {reassignDropdownOpen && reassignDropdownRect && createPortal(
                    <>
                      <div className="fixed inset-0 z-[60]" onClick={() => setReassignDropdownOpen(false)} />
                      <div
                        ref={reassignMenuRef}
                        className="fixed z-[70] bg-[color:var(--app-surface)] border border-[color:var(--app-border)] rounded-2xl shadow-2xl p-2 overflow-auto space-y-1"
                        style={{
                          top: Math.round(reassignDropdownRect.bottom + 8),
                          left: Math.round(reassignDropdownRect.left),
                          width: Math.round(reassignDropdownRect.width),
                          maxHeight: Math.min(272, window.innerHeight - reassignDropdownRect.bottom - 24)
                        }}
                        role="listbox"
                      >
                        <button
                          type="button"
                          className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                            deleteReassignTo === NO_LOCATION_KEY ? 'bg-[color:var(--app-surface-muted)]' : 'hover:bg-[color:var(--app-input-bg)] focus:bg-[color:var(--app-input-bg)]'
                          }`}
                          onClick={() => { setDeleteReassignTo(NO_LOCATION_KEY); setReassignDropdownOpen(false); }}
                        >
                          No location
                        </button>
                        {locations
                          .slice()
                          .filter(l => l.nickname !== editLocNickOriginal)
                          .sort((a, b) => (a.nickname || '').toLowerCase().localeCompare((b.nickname || '').toLowerCase()))
                          .map(l => (
                            <button
                              key={l.nickname}
                              type="button"
                              className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                                deleteReassignTo === l.nickname ? 'bg-[color:var(--app-surface-muted)]' : 'hover:bg-[color:var(--app-input-bg)] focus:bg-[color:var(--app-input-bg)]'
                              }`}
                              onClick={() => { setDeleteReassignTo(l.nickname); setReassignDropdownOpen(false); }}
                            >
                              {l.nickname}
                            </button>
                          ))}
                      </div>
                    </>,
                    document.body
                  )}
                </div>
              </div>
              <div className="flex justify-between mt-4">
                <button
                  type="button"
                  onClick={() => { setConfirmingDeleteLoc(false); setDeleteReassignTo(NO_LOCATION_KEY); setReassignDropdownOpen(false); }}
                  className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const destination = deleteReassignTo || NO_LOCATION_KEY;
                    cascadeDeleteLocation(editLocNickOriginal, destination);
                    closeEditLocationToManage();
                  }}
                  className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
      </>
    );
  };

  // Return section tasks ordered by stored per-location order; leftovers follow base list order
  // Additionally, ALWAYS render actives before completed within the section (stable within groups)
  const orderTasksWithinSection = (sectionTasks, sectionKey) => {
    const byList = locationOrderByList[selectedListId] || {};
    const stored = Array.isArray(byList[sectionKey]) ? byList[sectionKey] : [];

    // IDs actually present in this section right now
    const idsInSection = sectionTasks.map(t => t.id);

    // Keep stored order first (only the ids that still exist), then any leftovers
    const fromStored = stored.filter(id => idsInSection.includes(id));
    const leftovers  = idsInSection.filter(id => !fromStored.includes(id));
    const mergedIds  = [...fromStored, ...leftovers];

    const byId = new Map(sectionTasks.map(t => [t.id, t]));
    const merged = mergedIds.map(id => byId.get(id)).filter(Boolean);

    // Stable partition: actives first, then dones
    const actives = merged.filter(t => !t.done);
    const dones   = merged.filter(t =>  t.done);
    return [...actives, ...dones];
  };

// Group tasks by location nickname ("No location" FIRST, then alphabetical)
const groupTasksByLocation = (arr) => {
  const buckets = new Map();
  arr.forEach(t => {
    const key = (t?.details?.locationNickname && typeof t.details.locationNickname === 'string')
      ? t.details.locationNickname
      : NO_LOCATION_KEY;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(t);
  });

  const keys = Array.from(buckets.keys());
  const named = keys
    .filter(k => k !== NO_LOCATION_KEY)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const ordered = [NO_LOCATION_KEY, ...named];
  return ordered.filter(k => buckets.has(k)).map(k => ({ key: k, tasks: buckets.get(k) }));
};

  // Drag handler for Location sort: update only per-location order and task.locationNickname when crossing sections
  const onLocationDragEnd = (result) => {
    const { source, destination, draggableId } = result || {};
    if (!destination) return;
    const fromKey = source?.droppableId;
    const toKey = destination?.droppableId;
    if (!fromKey || !toKey) return;

    const getSectionTasks = (arr, key) => (
      groupTasksByLocation(arr).find(g => g.key === key)?.tasks || []
    );
    const doneById = {};
    tasks.forEach(t => { doneById[t.id] = !!t.done; });
    const buildStoredOrder = (key, renderedIds, byList) => {
      if (!hideCompleted) return renderedIds;
      const stored = Array.isArray(byList[key]) ? byList[key] : [];
      const allIds = orderTasksWithinSection(getSectionTasks(tasks, key), key).map(t => t.id);
      const allDoneIds = allIds.filter(id => doneById[id]);
      const doneFromStored = stored.filter(id => doneById[id]);
      const doneMissing = allDoneIds.filter(id => !stored.includes(id));
      const doneIds = [...doneFromStored, ...doneMissing];
      return [...renderedIds, ...doneIds];
    };

    if (fromKey === toKey) {
      // Reorder within the same location section (do not touch base tasks order)
      setLocationOrderByList(prev => {
        const byList = { ...(prev[selectedListId] || {}) };
        const renderedOrder = orderTasksWithinSection(
          getSectionTasks(visibleTasks, fromKey),
          fromKey
        ).map(t => t.id);
        const next = Array.from(renderedOrder);
        const moved = next[source.index] || draggableId;
        const fromIndex = next.indexOf(moved);
        if (fromIndex > -1) next.splice(fromIndex, 1);
        next.splice(destination.index, 0, moved);
        return {
          ...prev,
          [selectedListId]: { ...byList, [fromKey]: buildStoredOrder(fromKey, next, byList) }
        };
      });
    } else {
      // Move across sections: update both per-location orders and the task's locationNickname
      setLocationOrderByList(prev => {
        const byList = { ...(prev[selectedListId] || {}) };
        const renderedFrom = orderTasksWithinSection(
          getSectionTasks(visibleTasks, fromKey),
          fromKey
        ).map(t => t.id);
        const renderedTo = orderTasksWithinSection(
          getSectionTasks(visibleTasks, toKey),
          toKey
        ).map(t => t.id);
        const fromArr = Array.from(renderedFrom);
        const toArr = Array.from(renderedTo);
        const moved = fromArr[source.index] || draggableId;
        const idx = fromArr.indexOf(moved);
        if (idx > -1) fromArr.splice(idx, 1);
        toArr.splice(destination.index, 0, moved);
        const nextByList = {
          ...byList,
          [fromKey]: buildStoredOrder(fromKey, fromArr, byList),
          [toKey]: buildStoredOrder(toKey, toArr, byList)
        };
        return { ...prev, [selectedListId]: nextByList };
      });

      // Update the task's locationNickname without reordering base tasks array
      setTasks(prev => prev.map(t => (
        t.id === draggableId
          ? { ...t, details: { ...(t.details || {}), locationNickname: toKey === NO_LOCATION_KEY ? undefined : toKey } }
          : t
      )));
    }
  };
  // --------- Main Render with overlay slide transitions ---------
  return (
    <div
      className="relative h-screen min-h-screen overflow-hidden bg-[color:var(--app-bg)]"
      style={{ height: '100dvh' }}
    >
      {/* Global hidden input for importing backups (always mounted) */}
      <input
        ref={importInputRef}
        type="file"
        accept="application/json"
        className="hidden"
        name="backup-import"
        id="backup-import"
        onChange={handleImportFile}
      />
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 z-[29] bg-[color:var(--app-header-bg)]"
        style={{ height: "env(safe-area-inset-top)", pointerEvents: "none" }}
      />
      {/* Sticky header */}
      <div
        className="fixed left-0 right-0 z-[30] bg-[color:var(--app-header-bg)] backdrop-blur supports-[backdrop-filter]:bg-[color:var(--app-header-bg)]"
        style={{ top: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-md mx-auto px-4 sm:px-8 h-14 flex items-center justify-between relative">
          {/* Back chevron fades in/out, shown only on single-list page */}
          <AnimatePresence initial={false} mode="wait">
            {selectedListId != null ? (
              <motion.button
                key="back-btn"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                onClick={() => setSelectedListId(null)}
                aria-label="Back"
                type="button"
                className="text-[color:var(--app-text)] hover:text-[color:var(--app-text-secondary)] p-0 ml-[-8px] sm:ml-[-30px] md:ml-[-40px] lg:ml-[-40px]"
                style={{ minWidth: 24 }}
              >
                <ChevronLeft size={30} strokeWidth={3} />
              </motion.button>
            ) : (
              <motion.span key="back-spacer" style={{ width: 24, height: 24 }} />
            )}
          </AnimatePresence>

          {/* Right side: 3-dot menu trigger (static, does not slide) */}
          <div className="relative ml-auto">
            <span
              ref={headerMenuButtonRef}
              onMouseDown={(e) => { e.stopPropagation(); }}
              onClick={() => {
                setHeaderMenuOpen(prev => {
                  const next = !prev;
                  if (next) setSortMenuExpanded(false);
                  return next;
                });
              }}
              className="inline-flex mr-0 sm:mr-[-24px] md:mr-[-24px] lg:mr-[-24px]"
            >
              <MoreVertical className="text-[color:var(--app-text)] hover:text-[color:var(--app-text-secondary)] cursor-pointer p-0" />
            </span>
          </div>
          {/* container-level dropdown (aligned to content right edge) */}
          {headerMenuOpen && (
            <div
              ref={headerMenuRef}
              className="absolute top-full mt-2 right-4 sm:right-0 bg-[color:var(--app-input-bg)] border border-[color:var(--app-border)] rounded-2xl w-56 p-2 z-[70] shadow-2xl"
            >
              <div className="space-y-1">
                {selectedListId == null ? (
                  <>
                    <button
                      onClick={() => { setSelectedListId(RECENTLY_DELETED_ID); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      Recently deleted items
                    </button>
                    <div className="h-px bg-[color:var(--app-border)] mx-2" />
                    <button
                      onClick={() => { exportBackup(); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      Save a backup file
                    </button>
                    <button
                      onClick={() => { triggerImportBackup(); setTimeout(() => setHeaderMenuOpen(false), 0); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      Restore from backup
                    </button>
                    <div className="h-px bg-[color:var(--app-border)] mx-2" />
                    <button
                      onClick={() => { window.location.assign('/about'); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      About us
                    </button>
                  </>
                ) : selectedListId === RECENTLY_DELETED_ID ? (
                  <>
                    <button
                      onClick={() => { setShowDeleteAllDeletedModal(true); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      Delete All
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={event => {
                        setSortMenuExpanded(prev => !prev);
                        event.currentTarget.blur();
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] focus:outline-none transition ${
                        canHover ? 'hover:bg-[color:var(--app-surface)]' : ''
                      }`}
                      aria-expanded={sortMenuExpanded}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span>Sort by</span>
                        <ChevronDown
                          size={18}
                          strokeWidth={3}
                          className={`text-[color:var(--app-text)] transition-transform ${sortMenuExpanded ? 'rotate-180' : 'rotate-0'}`}
                        />
                      </span>
                    </button>
                    {sortMenuExpanded && [
                      ['priority', 'Priority'],
                      ['project', 'Project'],
                      ['location', 'Location']
                    ].map(([mode, label]) => {
                      const selected = ((sortModeByList && sortModeByList[selectedListId]) || 'priority') === mode;
                      return (
                        <button
                          key={mode}
                          onClick={event => {
                            setSortModeByList(prev => ({ ...(prev || {}), [selectedListId]: mode }));
                            setHeaderMenuOpen(false);
                            event.currentTarget.blur();
                          }}
                          className={`w-full text-left pl-8 pr-4 py-3 rounded-xl text-[color:var(--app-text)] transition ${
                            selected
                              ? 'bg-[#efefef] dark:bg-[#18181b] font-semibold'
                              : canHover
                                ? 'hover:bg-[#efefef] focus:bg-[#efefef] dark:hover:bg-[#18181b] dark:focus:bg-[#18181b]'
                                : 'focus:outline-none'
                          }`}
                          aria-current={selected ? 'true' : undefined}
                        >
                          {label}
                        </button>
                      );
                    })}
                    <div className="h-px bg-[color:var(--app-border)] mx-2" />
                    <button
                      onClick={() => { deleteCompleted(); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      Clear completed
                    </button>
                    <button
                      onClick={() => { setHideCompleted(prev => !prev); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      {hideCompleted ? 'Show completed' : 'Hide completed'}
                    </button>
                    <div className="h-px bg-[color:var(--app-border)] mx-2" />
                    <button
                      onClick={() => { setShowDeleteListModal(true); setHeaderMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[color:var(--app-text)] hover:bg-[color:var(--app-surface)] focus:bg-[color:var(--app-surface)] transition"
                    >
                      Delete this list
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      { !restoreDone ? (
        <div className="min-h-screen bg-[color:var(--app-bg)]" />
      ) : (
      <AnimatePresence initial={false}>
        {selectedListId == null ? (
          // --- Overview branch (unchanged) ---
          <motion.div
            key="overview"
            className="min-h-screen bg-[color:var(--app-bg)] flex items-start justify-center px-4 sm:px-8 py-4 pt-[calc(env(safe-area-inset-top)+3.625rem)] absolute inset-0 overflow-y-auto"
            style={{ zIndex: 1 }}
            ref={setOverviewScrollNode}
            initial={suppressFirstAnim ? false : { x: '-100%' }}
            animate={{ x: 0 }}
            exit={suppressFirstAnim ? { x: 0, transition: { duration: 0 } } : { x: '-100%' }}
            transition={suppressFirstAnim ? { duration: 0 } : slideTransition}
            onAnimationStart={resetActiveViewScroll}
            onAnimationComplete={resetActiveViewScroll}
          >
            <div className="relative w-full max-w-md">
          <div className="flex items-center mb-7" style={{ minWidth: 0, width: '100%' }}>
            <h1 className="text-3xl font-bold text-[color:var(--app-text)]" style={{ marginTop: '10px' }}>Lists</h1>
          </div>
          <DndContext
            sensors={sensors}
            modifiers={[restrictToParentElement]}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const oldIndex = lists.findIndex(l => l.id === active.id);
              const newIndex = lists.findIndex(l => l.id === over.id);
              if (oldIndex === -1 || newIndex === -1) return;
              setLists(prev => arrayMove(prev, oldIndex, newIndex));
            }}
          >
            <SortableContext items={lists.map(l => l.id)} strategy={rectSortingStrategy}>
              <div ref={overviewGridRef} className="grid grid-cols-2 gap-6 items-stretch mb-8">
                {lists.length === 0 ? (
                  <>
                    <div className="col-span-2 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setShowAddListModal(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowAddListModal(true);
                          }
                        }}
                        aria-label="Add list"
                      className="relative rounded-2xl border-2 border-[color:var(--app-border)] text-[color:var(--app-text-secondary)] hover:text-[color:var(--app-text)] hover:bg-[#fafafa] dark:hover:bg-[color:var(--app-card-hover)] transition-colors shadow-sm hover:shadow-md dark:hover:shadow-md p-6 min-h-[96px] w-[calc(50%-0.75rem)] flex items-left justify-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]"
                      >
                        <div className="flex flex-col items-left gap-2 select-none">
                          <Plus size={20} strokeWidth={3} aria-hidden="true" className="text-[color:var(--app-text-muted)]" />
                           <span className="text-sm font-normal text-[color:var(--app-text-muted)]">Add list</span>
                        </div>
                      </button>
                    </div>
                    <div className="col-span-2 text-[color:var(--app-text-muted)] text-center">Add a list to get started</div>
                  </>
                ) : (
                  <>
                    {lists.map((list) => (
                      <SortableListCard key={list.id} list={list} onOpen={() => setSelectedListId(list.id)} />
                    ))}
                    {/* Inline Add-a-list tile (matches list card size) */}
                    <button
                      type="button"
                      onClick={() => setShowAddListModal(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowAddListModal(true);
                        }
                      }}
                      aria-label="Add list"
                      data-list-card="true"
                      style={{ minHeight: uniformCardHeight ? uniformCardHeight : undefined }}
                      className="relative rounded-2xl border-2 border-[color:var(--app-border)] text-[color:var(--app-text-secondary)] hover:text-[color:var(--app-text)] hover:bg-[#fafafa] dark:hover:bg-[color:var(--app-card-hover)] transition-colors shadow-sm hover:shadow-md dark:hover:shadow-md p-6 min-h-[96px] flex items-center justify-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]"
                    >
                      <div className="flex flex-col items-left gap-2 select-none">
                        <Plus size={20} strokeWidth={3} aria-hidden="true" className="text-[color:var(--app-text-muted)]" />
                         <span className="text-sm font-normal text-[color:var(--app-text-muted)]">Add list</span>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </SortableContext>
          </DndContext>
          <div className="flex">
                <Modal
                  isOpen={showAddListModal}
                  onRequestClose={() => {
                    setShowAddListModal(false);
                    setNewListName("");
                    setAddListSortChoice('priority');
                  }}
                  className="bg-[color:var(--app-surface)] rounded-2xl w-full max-w-md mx-auto border-none shadow-2xl relative outline-none flex flex-col"
                  overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
                  closeTimeoutMS={modalDurationMs}
                  contentElement={(props, children) => (
                    <motion.div
                      {...props}
                      initial={{ y: '100vh' }}
                      animate={showAddListModal ? { y: 0 } : { y: '100vh' }}
                      transition={modalTransition}
                      style={{ willChange: 'transform' }}
                    >
                      {children}
                    </motion.div>
                  )}
                >
                  {/* Close */}
                  <button
                    className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
                    onClick={() => {
                      setShowAddListModal(false);
                      setNewListName("");
                      setAddListSortChoice('priority');
                    }}
                    aria-label="Close"
                    type="button"
                  >
                    <X size={22} />
                  </button>

                  {/* Body */}
                  <div className="px-6 sm:px-8 pt-9 pb-4 sm:pb-5">
                    <div className="space-y-6">
                      {/* List name */}
                      <div>
                        <label className="flex items-center gap-2 mb-2 text-[color:var(--app-text)] font-semibold">
                          <ListIcon size={18} />
                          List
                        </label>
                        <input
                          placeholder="List name"
                          value={newListName}
                          onChange={(e) => setNewListName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleCreateList();
                            }
                          }}
                          className="w-full bg-[color:var(--app-input-bg)] text-[color:var(--app-text)] rounded-2xl px-3.5 py-2 sm:px-4 sm:py-2.5 border border-[color:var(--app-border)] focus:outline-none transition text-base shadow-sm"
                          type="text"
                          inputMode="text"
                          autoCapitalize="words"
                          autoCorrect="off"
                          autoFocus
                        />
                      </div>

                      {/* Sort by (single row) */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[color:var(--app-text)] font-semibold">
                          <SortDesc size={18} />
                          <span>Sort by</span>
                        </div>
                        <div className="inline-flex items-center rounded-2xl bg-[color:var(--app-input-bg)] p-1">
                          {[
                            ['priority', 'Priority'],
                            ['project', 'Project'],
                            ['location', 'Location']
                          ].map(([mode, label], idx) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => setAddListSortChoice(mode)}
                              className={`${idx > 0 ? 'ml-1' : ''} px-3 py-1.5 rounded-xl font-medium transition ${
                                addListSortChoice === mode
                                  ? 'bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] shadow-sm'
                                  : 'text-[color:var(--app-text-secondary)] hover:text-[color:var(--app-text)]'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 sm:px-8 pb-5 flex justify-between">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddListModal(false);
                        setNewListName("");
                        setAddListSortChoice('priority');
                      }}
                      className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateList}
                      className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
                    >
                      Done
                    </button>
                  </div>
                </Modal>
          </div>
            </div>
          </motion.div>
        ) : selectedListId === RECENTLY_DELETED_ID ? (
          <motion.div
            key="recently-deleted"
            className="min-h-screen bg-[color:var(--app-bg)] flex items-start justify-center px-4 sm:px-8 py-4 pt-[calc(env(safe-area-inset-top)+3.625rem)] absolute inset-0 overflow-y-auto"
            style={{ zIndex: 2 }}
            ref={setRecentlyDeletedScrollNode}
            initial={suppressFirstAnim ? false : { x: '100%' }}
            animate={{ x: 0 }}
            exit={suppressFirstAnim ? { x: 0, transition: { duration: 0 } } : { x: '100%' }}
            transition={suppressFirstAnim ? { duration: 0 } : slideTransition}
            onAnimationStart={resetActiveViewScroll}
            onAnimationComplete={resetActiveViewScroll}
          >
            <div className="w-full max-w-md">
              <div className="mb-8" style={{ marginTop: '10px' }}>
                <div className="flex items-center" style={{ minWidth: 0, width: '100%' }}>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-3xl font-bold text-[color:var(--app-text)] truncate">Recently Deleted</h1>
                    <p className="text-[color:var(--app-text-muted)] mt-1">Items here are permanently deleted after 30 days</p>
                  </div>
                </div>
              </div>

              <ul className="space-y-4 pb-16">
                {recentlyDeleted.filter(it => (Date.now() - (it.deletedAt || 0)) <= RECENT_TTL_MS).length === 0 ? (
                  <li className="text-[color:var(--app-text-muted)] text-center py-8">No recently deleted items</li>
                ) : (
                  recentlyDeleted
                    .filter(it => (Date.now() - (it.deletedAt || 0)) <= RECENT_TTL_MS)
                    .sort((a,b) => (b.deletedAt||0) - (a.deletedAt||0))
                    .map(it => (
                      <li key={it.id}>
                        <button
                          className="w-full text-left flex items-center bg-[color:var(--app-surface)] rounded-2xl px-4 py-2 min-h-[48px] transition-shadow hover:shadow-sm dark:transition-colors dark:hover:bg-[color:var(--app-surface-hover)]"
                          onClick={() => { setRecoverItemId(it.id); setShowRecoverModal(true); }}
                        >
                          {/* Non-interactive radio indicator (matches single list style) */}
                          <div className="w-6 h-6 border-2 border-[color:var(--app-text)] rounded-full flex items-center justify-center mr-4 select-none">
                            {it.task?.done ? <div className="w-3 h-3 bg-[color:var(--app-text)] rounded-full" /> : null}
                          </div>
                          {/* Title only */}
                          <div className="flex-1 min-w-0">
                            <div className="text-[color:var(--app-text)] break-words whitespace-normal">{it.task?.text || '(untitled)'}</div>
                          </div>
                        </button>
                      </li>
                    ))
                )}
              </ul>

              {/* Recover modal */}
              <Modal
                isOpen={showRecoverModal}
                onRequestClose={() => setShowRecoverModal(false)}
                className="bg-[color:var(--app-surface)] rounded-2xl p-8 w-full max-w-xs mx-auto border-none shadow-2xl relative outline-none"
                overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
                closeTimeoutMS={modalDurationMs}
                contentElement={(props, children) => (
                  <motion.div {...props} initial={{ y: '100vh' }} animate={showRecoverModal ? { y: 0 } : { y: '100vh' }} transition={modalTransition} style={{ willChange: 'transform' }}>
                    {children}
                  </motion.div>
                )}
              >
                <button
                  className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
                  onClick={() => setShowRecoverModal(false)}
                  aria-label="Close"
                  type="button"
                >
                  <X size={22} />
                </button>
                <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-6">Recover this item?</h2>
                <p className="text-[color:var(--app-text-secondary)] mb-6">This will move it back to the list it was included in.</p>
                <div className="flex justify-between gap-3">
                  <button
                    onClick={() => setShowRecoverModal(false)}
                    className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
                  >
                    Cancel
                  </button>
                    <button
                      onClick={() => {
                        const entry = recentlyDeleted.find(e => e.id === recoverItemId);
                        if (entry?.task) {
                          const toListId = entry.fromListId;
                          const exists = lists.some(l => l.id === toListId);
                          if (exists) {
                            setLists(prev => prev.map(l => {
                              if (l.id !== toListId) return l;
                              const curr = Array.isArray(l.tasks) ? l.tasks : [];
                              const newTasks = entry.task.done ? [...curr, entry.task] : [entry.task, ...curr];
                              return { ...l, tasks: newTasks };
                            }));
                            if (selectedListId === toListId) {
                              setTasks(prev => entry.task.done ? [...prev, entry.task] : [entry.task, ...prev]);
                            }
                          } else {
                            // If a list named "Recovered" already exists, add to it; otherwise create it
                            const recoveredExisting = lists.find(l => (l.title || '').trim().toLowerCase() === 'recovered');
                            if (recoveredExisting) {
                              setLists(prev => prev.map(l => {
                                if (l.id !== recoveredExisting.id) return l;
                                const curr = Array.isArray(l.tasks) ? l.tasks : [];
                                const newTasks = entry.task.done ? [...curr, entry.task] : [entry.task, ...curr];
                                return { ...l, tasks: newTasks };
                              }));
                              if (selectedListId === recoveredExisting.id) {
                                setTasks(prev => entry.task.done ? [...prev, entry.task] : [entry.task, ...prev]);
                              }
                              openNotice('Added to Recovered list', 'Original list was missing, added to "Recovered" list.');
                            } else {
                              const newId = crypto.randomUUID();
                              const newListTasks = [entry.task];
                              const newList = { id: newId, title: 'Recovered', tasks: newListTasks, hideCompleted: false };
                              setLists(prev => [newList, ...prev]);
                              openNotice('Recovered to new list', 'Original list was missing, created a "Recovered" list.');
                            }
                          }
                          setRecentlyDeleted(prev => prev.filter(e => e.id !== recoverItemId));
                        }
                        setShowRecoverModal(false);
                      }}
                      className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
                    >
                      Recover
                    </button>
                </div>
              </Modal>

              {/* Delete All modal for Recently Deleted */}
              <Modal
                isOpen={showDeleteAllDeletedModal}
                onRequestClose={() => setShowDeleteAllDeletedModal(false)}
                className="bg-[color:var(--app-surface)] rounded-2xl p-8 w-full max-w-xs mx-auto border-none shadow-2xl relative outline-none"
                overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
                closeTimeoutMS={modalDurationMs}
                contentElement={(props, children) => (
                  <motion.div {...props} initial={{ y: '100vh' }} animate={showDeleteAllDeletedModal ? { y: 0 } : { y: '100vh' }} transition={modalTransition} style={{ willChange: 'transform' }}>
                    {children}
                  </motion.div>
                )}
              >
                <button
                  className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
                  onClick={() => setShowDeleteAllDeletedModal(false)}
                  aria-label="Close"
                  type="button"
                >
                  <X size={22} />
                </button>
                <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-6">Delete All?</h2>
                <p className="text-[color:var(--app-text-secondary)] mb-6">All items in Recently Deleted will be permanently deleted.</p>
                <div className="flex justify-between gap-3">
                  <button
                    onClick={() => setShowDeleteAllDeletedModal(false)}
                    className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { setRecentlyDeleted([]); setShowDeleteAllDeletedModal(false); }}
                    className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
                  >
                    Delete
                  </button>
                </div>
              </Modal>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={`single-${selectedListId}`}
            className="min-h-screen bg-[color:var(--app-bg)] flex items-start justify-center px-4 sm:px-8 py-4 pt-[calc(env(safe-area-inset-top)+3.625rem)] absolute inset-0 overflow-y-auto"
            style={{ zIndex: 2 }}
            ref={setListScrollNode}
            initial={suppressFirstAnim ? false : { x: '100%' }}
            animate={{ x: 0 }}
            exit={suppressFirstAnim ? { x: 0, transition: { duration: 0 } } : { x: '100%' }}
            transition={suppressFirstAnim ? { duration: 0 } : slideTransition}
            onAnimationStart={resetActiveViewScroll}
            onAnimationComplete={resetActiveViewScroll}
          >
            <div className="w-full max-w-md">
        <div className="mb-8" style={{ marginTop: '10px' }}>
          <div className="flex items-center" style={{ minWidth: 0, width: '100%' }}>
            <div className="flex-1 min-w-0">
              {editingTitle ? (
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  onBlur={finalizeTitleEditing}
                  onKeyDown={e => e.key === "Enter" && finalizeTitleEditing()}
                  autoFocus
                  className="text-3xl font-bold text-[color:var(--app-text)] bg-[color:var(--app-secondary-bg)] border-none focus:outline-none focus:ring-0 placeholder-transparent"
                />
              ) : (
                <h1
                  className="text-3xl font-bold text-[color:var(--app-text)] cursor-pointer truncate min-h-[2.25rem]"
                  onClick={beginEditingTitle}
                >
                  {title && title.trim() !== "" ? title : "To Do"}
                </h1>
              )}
            </div>
          </div>
        </div>
       
        {/* Delete List Confirmation Modal */}
        <Modal
          isOpen={showDeleteListModal}
          onRequestClose={() => setShowDeleteListModal(false)}
          className="bg-[color:var(--app-surface)] rounded-2xl p-8 w-full max-w-xs mx-auto border-none shadow-2xl relative outline-none"
          overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-50 pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
          closeTimeoutMS={modalDurationMs}
          contentElement={(props, children) => (
            <motion.div
              {...props}
              initial={{ y: '100vh' }}
              animate={showDeleteListModal ? { y: 0 } : { y: '100vh' }}
              transition={modalTransition}
              style={{ willChange: 'transform' }}
            >
              {children}
            </motion.div>
          )}
        >
            <button
              className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
              onClick={() => setShowDeleteListModal(false)}
              aria-label="Close"
              type="button"
            >
              <X size={22} />
            </button>
            <h2 className="text-xl font-bold text-[color:var(--app-text)] mb-6">Delete this list?</h2>
            <p className="text-[color:var(--app-text-secondary)] mb-6">Are you sure you want to delete <b>{title}</b>? This cannot be undone.</p>
            <div className="flex justify-between gap-3">
              <button
                onClick={() => handleDeleteList(selectedListId)}
                className="bg-[color:var(--app-secondary-bg)] border border-[color:var(--app-secondary-border)] text-[color:var(--app-secondary-text)] px-5 py-2 rounded-2xl font-medium transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-secondary-hover)] hover:text-[color:var(--app-text)]"
              >
                Delete List
              </button>
              <button
                onClick={() => setShowDeleteListModal(false)}
                className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] px-6 py-2 rounded-2xl font-bold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
              >
                Cancel
              </button>
            </div>
        </Modal>
        {/* New Task Input & Task List */}
        <form
          className={`flex items-center ${visibleTasks.length > 0 ? "mb-4" : ""}`}
          onSubmit={(event) => {
            event.preventDefault();
            addTask();
          }}
        >
          <label htmlFor="new-task-input" className="sr-only">Add a task</label>
          <input
            id="new-task-input"
            name="new-task-input"
            placeholder="Add a task"
            enterKeyHint="send"
            className="placeholder-[color:var(--app-text-muted)] text-base flex-1 bg-[color:var(--app-surface)] text-[color:var(--app-text)] rounded-l-2xl px-4 h-12 focus:outline-none focus:ring-0"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTask()}
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: "none"
            }}
          />
          <button
            type="submit"
            aria-label="Add task"
            className="group bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] rounded-r-2xl h-12 w-12 flex items-center justify-center transition-colors shadow-sm hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] active:shadow-sm"
            style={{
              marginLeft: 0,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderLeft: "none"
            }}
          >
            <Plus size={18} className="opacity-90 transition-opacity group-hover:opacity-100" />
          </button>
        </form>
        {/* Task List */}
        <DragDropContext
          autoScrollerOptions={{ disabled: true }}
          onDragStart={(start) => {
            if (dragScrollLockRef.current) return;
            isDraggingRef.current = true;
            setRevealedInfoTaskId(null);
            projectDraggingTaskIdRef.current = start?.draggableId || null;
            setProjectDropTarget(null);
            startAutoScrollLoop();
            const scrollEl = listScrollRef.current;
            dragScrollLockRef.current = {
              overflow: document.body.style.overflow,
              overscrollBehaviorY: document.body.style.overscrollBehaviorY,
              scrollOverflowY: scrollEl ? scrollEl.style.overflowY : null,
              scrollOverscrollBehaviorY: scrollEl ? scrollEl.style.overscrollBehaviorY : null
            };
            document.body.style.overflow = 'hidden';
            document.body.style.overscrollBehaviorY = 'contain';
            if (scrollEl) {
              scrollEl.style.overflowY = 'hidden';
              scrollEl.style.overscrollBehaviorY = 'contain';
            }
          }}
          onDragUpdate={(update) => {
            const mode = (sortModeByList[selectedListId] || 'priority');
            if (mode !== 'project') {
              setProjectDropTarget(null);
              return;
            }
            const draggedId = update?.draggableId || projectDraggingTaskIdRef.current;
            const combineTargetId = getProjectDropTargetFromTaskId(draggedId, update?.combine?.draggableId);
            if (combineTargetId) {
              setProjectDropTarget(combineTargetId);
              return;
            }
            const overlappedTargetId = getProjectDropTargetFromOverlappedChildBlock(draggedId);
            if (overlappedTargetId) {
              setProjectDropTarget(overlappedTargetId);
              return;
            }
            const pointerTargetId = findProjectDropTargetFromPointer(draggedId);
            if (pointerTargetId) {
              setProjectDropTarget(pointerTargetId);
              return;
            }
            setProjectDropTarget(getProjectDropTargetFromDestination(draggedId, update?.destination));
          }}
          onDragEnd={(result) => {
            const mode = (sortModeByList[selectedListId] || 'priority');
            if (mode === 'location') {
              onLocationDragEnd(result);
              isDraggingRef.current = false;
              projectDraggingTaskIdRef.current = null;
              setProjectDropTarget(null);
              stopAutoScrollLoop();
              if (dragScrollLockRef.current) {
                document.body.style.overflow = dragScrollLockRef.current.overflow;
                document.body.style.overscrollBehaviorY = dragScrollLockRef.current.overscrollBehaviorY;
                if (listScrollRef.current) {
                  listScrollRef.current.style.overflowY = dragScrollLockRef.current.scrollOverflowY;
                  listScrollRef.current.style.overscrollBehaviorY = dragScrollLockRef.current.scrollOverscrollBehaviorY;
                }
                dragScrollLockRef.current = null;
              }
              return;
            }
            handleDragEnd(result);
            isDraggingRef.current = false;
            projectDraggingTaskIdRef.current = null;
            setProjectDropTarget(null);
            stopAutoScrollLoop();
            if (dragScrollLockRef.current) {
              document.body.style.overflow = dragScrollLockRef.current.overflow;
              document.body.style.overscrollBehaviorY = dragScrollLockRef.current.overscrollBehaviorY;
              if (listScrollRef.current) {
                listScrollRef.current.style.overflowY = dragScrollLockRef.current.scrollOverflowY;
                listScrollRef.current.style.overscrollBehaviorY = dragScrollLockRef.current.scrollOverscrollBehaviorY;
              }
              dragScrollLockRef.current = null;
            }
          }}
        >
          {(sortModeByList[selectedListId] || 'priority') === 'location' ? (
            <div className="space-y-1 pb-16">
              {groupTasksByLocation(visibleTasks).map(section => (
                <div key={section.key}>
                  <button
                    type="button"
                    onClick={() => setCollapsedSections(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
                    className={`w-full text-left ${collapsedSections[section.key] ? 'mb-0' : 'mb-2'}`}
                  >
                    <div className="flex items-center gap-2">
                      <ChevronDown
                        size={18}
                        strokeWidth={3}
                        className={`text-[color:var(--app-text)] transition-transform ${collapsedSections[section.key] ? '-rotate-90' : 'rotate-0'}`}
                      />
                      <span className="text-[color:var(--app-text)] text-xl font-semibold">
                        {section.key === NO_LOCATION_KEY ? 'No location' : section.key}
                      </span>
                    </div>
                  </button>

                  {!collapsedSections[section.key] && (
                    <Droppable droppableId={section.key}>
                      {provided2 => (
                        <ul className="pb-4" ref={provided2.innerRef} {...provided2.droppableProps}>
                        {(() => {
                          const orderedSectionTasks = orderTasksWithinSection(section.tasks, section.key);
                          return orderedSectionTasks.map((task, index) => {
                            return (
                              <Draggable key={task.id} draggableId={task.id} index={index}>
                                {(prov, snapshot) => {
                                  const showSwipeInfo = !canHover && revealedInfoTaskId === task.id && !snapshot.isDragging;
                                  const showHoverInfo = canHover && !snapshot.isDragging;
                                  const draggableStyle = {
                                    ...prov.draggableProps.style,
                                    transition: snapshot.isDropAnimating
                                      ? 'transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)'
                                      : prov.draggableProps.style?.transition
                                  };
                                  return (
                                  <li
                                    ref={prov.innerRef}
                                    style={draggableStyle}
                                    {...prov.draggableProps}
                                    {...prov.dragHandleProps}
                                    onPointerDown={event => handleTaskPointerDown(task.id, event)}
                                    data-task-swipe-row="true"
                                    className={`group relative min-h-[48px] rounded-2xl mb-4 ${
                                      snapshot.isDragging ? 'opacity-70' : ''
                                    }`}
                                  >
                                  <div
                                    className={`flex items-center bg-[color:var(--app-surface)] rounded-2xl pl-[12px] pr-[9px] py-2 min-h-[48px] ${snapshot.isDropAnimating ? 'transition-none' : 'transition-transform duration-200'} ${
                                      showSwipeInfo ? '-translate-x-[4.5rem]' : 'translate-x-0'
                                    }`}
                                  >
                                    {/* Checkbox */}
                                    <div
                                      onClick={() => handleCheck(task.id)}
                                      className="w-6 h-6 shrink-0 border-2 border-[color:var(--app-text)] rounded-full flex items-center justify-center cursor-pointer mr-[12px]"
                                    >
                                      {task.done && animatingTask !== task.id && <div className="w-3 h-3 bg-[color:var(--app-text)] rounded-full" />}
                                      {animatingTask === task.id && (
                                        <motion.div
                                          className="w-3 h-3 bg-[color:var(--app-text)] rounded-full"
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          transition={{ duration: 0.18 }}
                                        />
                                      )}
                                    </div>

                                    {/* Text / editor (same as your current block) */}
                                    <div className="flex-1 min-w-0 relative pr-[34px]">
                                      {editingTaskId === task.id ? (
                                        <textarea
                                        ref={editingTextareaRef}
                                        value={task.text}
                                        onChange={e => {
                                          if (e.target.value !== task.text) {
                                            updateTask(task.id, 'text', e.target.value);
                                          }
                                          const el = editingTextareaRef.current;
                                          if (el) {
                                            el.style.boxSizing = 'border-box';
                                            const cs = window.getComputedStyle(el);
                                            const lh = parseFloat(cs.lineHeight || '0');
                                            if (lh) el.style.minHeight = lh + 'px';
                                            el.style.height = 'auto';
                                            el.style.height = el.scrollHeight + 'px';
                                          }
                                        }}
                                        onInput={() => {
                                          const el = editingTextareaRef.current;
                                          if (el) {
                                            el.style.boxSizing = 'border-box';
                                            const cs = window.getComputedStyle(el);
                                            const lh = parseFloat(cs.lineHeight || '0');
                                            if (lh) el.style.minHeight = lh + 'px';
                                            el.style.height = 'auto';
                                            el.style.height = el.scrollHeight + 'px';
                                          }
                                        }}
                                        onBlur={() => finalizeTaskEditing(task.id)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') finalizeTaskEditing(task.id);
                                        }}
                                        autoFocus
                                        rows={1}
                                        className="block w-full bg-[color:var(--app-surface)] text-[color:var(--app-text)] pl-0 py-0 rounded-lg focus:outline-none focus:ring-0 resize-none overflow-hidden text-base leading-[1.5] m-0 border-0 box-border break-words"
                                        style={{
                                          fontSize: 'inherit',
                                          lineHeight: 'inherit',
                                          fontFamily: 'inherit',
                                          fontWeight: 'inherit',
                                          letterSpacing: 'inherit'
                                        }}
                                        id={`edit-task-${task.id}`}
                                        name={`edit-task-${task.id}`}
                                      />
                                    ) : (
                                      <div className="relative">
                                        <span
                                          ref={el => {
                                            if (el) textRefs.current[task.id] = el;
                                          }}
                                          className={`relative select-text break-words transition-colors duration-300 text-base leading-[1.5] ${
                                            task.done ? 'text-[color:var(--app-text-muted)] opacity-50 line-through' : 'text-[color:var(--app-text)]'
                                          } cursor-text`}
                                          onClick={() => startEditTask(task.id)}
                                        >
                                          {task.text}
                                          {animatingTask === task.id &&
                                            !task.done &&
                                            (strikeRectsByTask[task.id] || []).map((r, i) => (
                                              <motion.div
                                                key={i}
                                                className="absolute bg-[color:var(--app-text)]/90"
                                                style={{
                                                  left: r.left,
                                                  top: r.top,
                                                  height: 2,
                                                  width: r.width,
                                                  transform: 'translateY(-50%)'
                                                }}
                                                initial={{ scaleX: 0, originX: 0 }}
                                                animate={{ scaleX: 1 }}
                                                transition={{ duration: CROSS_OUT_DURATION }}
                                              />
                                            ))}
                                        </span>
                                        {renderTaskMetadataPills(task)}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className={`absolute right-[7px] top-1/2 -translate-y-1/2 text-black dark:text-[color:var(--app-text-muted)] rounded-full h-8 w-8 flex items-center justify-center transition-opacity ${
                                      editingTaskId === task.id
                                        ? 'opacity-100 hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] hover:bg-[color:var(--app-chip-bg)]'
                                        : showHoverInfo
                                          ? 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] hover:bg-[color:var(--app-chip-bg)]'
                                          : 'opacity-0 pointer-events-none'
                                    } focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]`}
                                    onClick={() => {
                                      setSelectedListForDialog(selectedListId);
                                      setOpenDialogId(task.id);
                                    }}
                                  >
                                    <Info size={18} />
                                  </button>

                                  </div>
                                  {/* Info button + Modal (unchanged trigger) */}
                                  <button
                                    className={`absolute right-0 top-1/2 -translate-y-1/2 text-black dark:text-[color:var(--app-text-muted)] bg-[color:var(--app-surface)] rounded-full h-12 w-12 flex items-center justify-center transition-opacity duration-200 hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] ${
                                      showSwipeInfo ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                    }`}
                                    onClick={() => {
                                      setSelectedListForDialog(selectedListId);
                                      setOpenDialogId(task.id);
                                    }}
                                    data-task-info-button="true"
                                  >
                                    <Info size={18} />
                                  </button>
                                </li>
                              );
                              }}
                              </Draggable>
                            );
                          });
                          })()}
                          <div className="mb-4">{provided2.placeholder}</div>
                        </ul>
                      )}
                    </Droppable>
                  )}
                  {collapsedSections[section.key] && <div className="h-4" />}
                </div>
              ))}
            </div>
          ) : (sortModeByList[selectedListId] || 'priority') === 'project' ? (
            <Droppable droppableId="project-outline" isCombineEnabled>
              {provided => (
                <div className="pb-16" {...provided.droppableProps} ref={provided.innerRef}>
                  {getProjectOutlineRows().map((row, index) => {
                    const task = row.task;
                    const parentCollapsed = collapsedProjects[task.id];
                    return (
                      <Draggable key={task.id} draggableId={task.id} index={index}>
                        {(prov, snapshot) => {
                          const showSwipeInfo = !canHover && revealedInfoTaskId === task.id && !snapshot.isDragging;
                          const showHoverInfo = canHover && !snapshot.isDragging;
                          const isProjectDropTarget = projectDropTargetId === task.id;
                          const draggableStyle = {
                            ...prov.draggableProps.style,
                            transition: snapshot.isDropAnimating
                              ? 'transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)'
                              : prov.draggableProps.style?.transition
                          };
                          return (
                            <div
                              ref={prov.innerRef}
                              style={draggableStyle}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              onPointerDown={event => handleTaskPointerDown(task.id, event)}
                              data-task-swipe-row="true"
                              data-project-row-type={row.type}
                              data-project-task-id={task.id}
                              data-project-parent-id={row.parentId || ''}
                              className={`${row.type === 'child' ? 'ml-8 ' : ''}group relative min-h-[48px] rounded-2xl mb-4 ${snapshot.isDragging ? 'opacity-70' : ''}`}
                            >
                              <div
                                className={`flex items-center bg-[color:var(--app-surface)] rounded-2xl pl-[12px] pr-[9px] py-2 min-h-[48px] ${
                                  isProjectDropTarget ? 'ring-1 ring-[color:var(--app-border-strong)] bg-[color:var(--app-surface-hover)] shadow-md scale-[1.005]' : ''
                                } ${snapshot.isDropAnimating ? 'transition-none' : 'transition-[transform,box-shadow,background-color] duration-200'} ${
                                  showSwipeInfo ? '-translate-x-[4.5rem]' : 'translate-x-0'
                                }`}
                              >
                                <div
                                  onClick={() => handleCheck(task.id)}
                                  className="w-6 h-6 shrink-0 border-2 border-[color:var(--app-text)] rounded-full flex items-center justify-center cursor-pointer mr-[12px]"
                                >
                                  {task.done && animatingTask !== task.id && <div className="w-3 h-3 bg-[color:var(--app-text)] rounded-full" />}
                                  {animatingTask === task.id && (
                                    <motion.div className="w-3 h-3 bg-[color:var(--app-text)] rounded-full" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.18 }} />
                                  )}
                                </div>
                                <div className={`flex-1 min-w-0 relative ${row.isParent ? 'pr-[68px]' : 'pr-[34px]'}`}>
                                  {editingTaskId === task.id ? (
                                    <textarea
                                      ref={editingTextareaRef}
                                      value={task.text}
                                      onChange={e => {
                                        if (e.target.value !== task.text) updateTask(task.id, 'text', e.target.value);
                                        const el = editingTextareaRef.current;
                                        if (el) {
                                          el.style.boxSizing = 'border-box';
                                          const cs = window.getComputedStyle(el);
                                          const lh = parseFloat(cs.lineHeight || '0');
                                          if (lh) el.style.minHeight = lh + 'px';
                                          el.style.height = 'auto';
                                          el.style.height = el.scrollHeight + 'px';
                                        }
                                      }}
                                      onInput={() => {
                                        const el = editingTextareaRef.current;
                                        if (el) {
                                          el.style.boxSizing = 'border-box';
                                          const cs = window.getComputedStyle(el);
                                          const lh = parseFloat(cs.lineHeight || '0');
                                          if (lh) el.style.minHeight = lh + 'px';
                                          el.style.height = 'auto';
                                          el.style.height = el.scrollHeight + 'px';
                                        }
                                      }}
                                      onBlur={() => finalizeTaskEditing(task.id)}
                                      onKeyDown={e => { if (e.key === 'Enter') finalizeTaskEditing(task.id); }}
                                      autoFocus
                                      rows={1}
                                      className="block w-full bg-[color:var(--app-surface)] text-[color:var(--app-text)] pl-0 py-0 rounded-lg focus:outline-none focus:ring-0 resize-none overflow-hidden text-base leading-[1.5] m-0 border-0 box-border break-words"
                                      style={{ fontSize: 'inherit', lineHeight: 'inherit', fontFamily: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit' }}
                                    />
                                  ) : (
                                    <div className="relative">
                                      <span
                                        ref={el => { if (el) textRefs.current[task.id] = el; }}
                                        className={`relative select-text break-words transition-colors duration-300 text-base leading-[1.5] ${
                                          task.done ? 'text-[color:var(--app-text-muted)] opacity-50 line-through' : 'text-[color:var(--app-text)]'
                                        } cursor-text`}
                                        onClick={() => startEditTask(task.id)}
                                      >
                                        {task.text}
                                      </span>
                                      {renderTaskMetadataPills(task)}
                                    </div>
                                  )}
                                </div>
                                <button
                                  className={`absolute ${row.isParent && (editingTaskId !== task.id || canHover) ? 'right-[39px]' : 'right-[7px]'} top-1/2 -translate-y-1/2 text-black dark:text-[color:var(--app-text-muted)] rounded-full h-8 w-8 flex items-center justify-center transition-opacity ${
                                    editingTaskId === task.id && (!row.isParent || !canHover)
                                      ? 'opacity-100 hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] hover:bg-[color:var(--app-chip-bg)]'
                                      : showHoverInfo || (editingTaskId === task.id && row.isParent && canHover)
                                        ? 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] hover:bg-[color:var(--app-chip-bg)]'
                                        : 'opacity-0 pointer-events-none'
                                  } focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]`}
                                  onClick={() => { setSelectedListForDialog(selectedListId); setOpenDialogId(task.id); }}
                                >
                                  <Info size={18} />
                                </button>
                                {row.isParent && (editingTaskId !== task.id || canHover) && (
                                  <button
                                    type="button"
                                    className={`absolute right-[7px] top-1/2 -translate-y-1/2 text-[color:var(--app-text)] rounded-full h-8 w-8 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] ${
                                      canHover ? 'hover:bg-[color:var(--app-chip-bg)]' : ''
                                    }`}
                                    onClick={event => {
                                      event.stopPropagation();
                                      updateCollapsedProjects(prev => ({ ...prev, [task.id]: !prev[task.id] }));
                                      event.currentTarget.blur();
                                    }}
                                    aria-label={parentCollapsed ? 'Expand project' : 'Collapse project'}
                                  >
                                    <ChevronDown size={18} strokeWidth={3} className={`transition-transform ${parentCollapsed ? '-rotate-90' : 'rotate-0'}`} />
                                  </button>
                                )}
                              </div>
                              <button
                                className={`absolute right-0 top-1/2 -translate-y-1/2 text-black dark:text-[color:var(--app-text-muted)] bg-[color:var(--app-surface)] rounded-full h-12 w-12 flex items-center justify-center transition-opacity duration-200 hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] ${
                                  showSwipeInfo ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                }`}
                                onClick={() => { setSelectedListForDialog(selectedListId); setOpenDialogId(task.id); }}
                                data-task-info-button="true"
                              >
                                <Info size={18} />
                              </button>
                            </div>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ) : (
            // PRIORITY mode (flat list)
            <Droppable droppableId="todo-list">
              {provided => (
                <ul className="pb-16" {...provided.droppableProps} ref={provided.innerRef}>
                  {visibleTasks.map((task, index) => {
                    return (
                      <Draggable key={task.id} draggableId={task.id} index={index}>
                        {(prov, snapshot) => {
                          const showSwipeInfo = !canHover && revealedInfoTaskId === task.id && !snapshot.isDragging;
                          const showHoverInfo = canHover && !snapshot.isDragging;
                          const draggableStyle = {
                            ...prov.draggableProps.style,
                            transition: snapshot.isDropAnimating
                              ? 'transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)'
                              : prov.draggableProps.style?.transition
                          };
                          return (
                          <li
                            ref={prov.innerRef}
                            style={draggableStyle}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            onPointerDown={event => handleTaskPointerDown(task.id, event)}
                            data-task-swipe-row="true"
                            className={`group relative min-h-[48px] rounded-2xl mb-4 ${
                              snapshot.isDragging ? 'opacity-70' : ''
                            }`}
                          >
                          <div
                            className={`flex items-center bg-[color:var(--app-surface)] rounded-2xl pl-[12px] pr-[9px] py-2 min-h-[48px] ${snapshot.isDropAnimating ? 'transition-none' : 'transition-transform duration-200'} ${
                              showSwipeInfo ? '-translate-x-[4.5rem]' : 'translate-x-0'
                            }`}
                          >
                            {/* Checkbox */}
                            <div
                              onClick={() => handleCheck(task.id)}
                              className="w-6 h-6 shrink-0 border-2 border-[color:var(--app-text)] rounded-full flex items-center justify-center cursor-pointer mr-[12px]"
                            >
                              {task.done && animatingTask !== task.id && <div className="w-3 h-3 bg-[color:var(--app-text)] rounded-full" />}
                              {animatingTask === task.id && (
                                <motion.div
                                  className="w-3 h-3 bg-[color:var(--app-text)] rounded-full"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ duration: 0.18 }}
                                />
                              )}
                            </div>
                            {/* Text / editor */}
                            <div className="flex-1 min-w-0 relative pr-[34px]">
                              {editingTaskId === task.id ? (
                                <textarea
                                ref={editingTextareaRef}
                                value={task.text}
                                onChange={e => {
                                  if (e.target.value !== task.text) {
                                    updateTask(task.id, 'text', e.target.value);
                                  }
                                  const el = editingTextareaRef.current;
                                  if (el) {
                                    el.style.boxSizing = 'border-box';
                                    const cs = window.getComputedStyle(el);
                                    const lh = parseFloat(cs.lineHeight || '0');
                                    if (lh) el.style.minHeight = lh + 'px';
                                    el.style.height = 'auto';
                                    el.style.height = el.scrollHeight + 'px';
                                  }
                                }}
                                onInput={() => {
                                  const el = editingTextareaRef.current;
                                  if (el) {
                                    el.style.boxSizing = 'border-box';
                                    const cs = window.getComputedStyle(el);
                                    const lh = parseFloat(cs.lineHeight || '0');
                                    if (lh) el.style.minHeight = lh + 'px';
                                    el.style.height = 'auto';
                                    el.style.height = el.scrollHeight + 'px';
                                  }
                                }}
                                onBlur={() => finalizeTaskEditing(task.id)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') finalizeTaskEditing(task.id);
                                }}
                                autoFocus
                                rows={1}
                                className="block w-full bg-[color:var(--app-surface)] text-[color:var(--app-text)] pl-0 py-0 rounded-lg focus:outline-none focus:ring-0 resize-none overflow-hidden text-base leading-[1.5] m-0 border-0 box-border break-words"
                                style={{
                                  fontSize: 'inherit',
                                  lineHeight: 'inherit',
                                  fontFamily: 'inherit',
                                  fontWeight: 'inherit',
                                  letterSpacing: 'inherit'
                                }}
                                id={`edit-task-${task.id}`}
                                name={`edit-task-${task.id}`}
                              />
                            ) : (
                              <div className="relative">
                                <span
                                  ref={el => {
                                    if (el) textRefs.current[task.id] = el;
                                  }}
                                  className={`relative select-text break-words transition-colors duration-300 text-base leading-[1.5] ${
                                    task.done ? 'text-[color:var(--app-text-muted)] opacity-50 line-through' : 'text-[color:var(--app-text)]'
                                  } cursor-text`}
                                  onClick={() => startEditTask(task.id)}
                                >
                                  {task.text}
                                  {animatingTask === task.id &&
                                    !task.done &&
                                    (strikeRectsByTask[task.id] || []).map((r, i) => (
                                      <motion.div
                                        key={i}
                                        className="absolute bg-[color:var(--app-text)]/90"
                                        style={{
                                          left: r.left,
                                          top: r.top,
                                          height: 2,
                                          width: r.width,
                                          transform: 'translateY(-50%)'
                                        }}
                                        initial={{ scaleX: 0, originX: 0 }}
                                        animate={{ scaleX: 1 }}
                                        transition={{ duration: CROSS_OUT_DURATION }}
                                      />
                                    ))}
                                </span>
                                {renderTaskMetadataPills(task)}
                              </div>
                            )}
                          </div>
                          <button
                            className={`absolute right-[7px] top-1/2 -translate-y-1/2 text-black dark:text-[color:var(--app-text-muted)] rounded-full h-8 w-8 flex items-center justify-center transition-opacity ${
                              editingTaskId === task.id
                                ? 'opacity-100 hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] hover:bg-[color:var(--app-chip-bg)]'
                                : showHoverInfo
                                  ? 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] hover:bg-[color:var(--app-chip-bg)]'
                                  : 'opacity-0 pointer-events-none'
                            } focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)]`}
                            onClick={() => {
                              setSelectedListForDialog(selectedListId);
                              setOpenDialogId(task.id);
                            }}
                          >
                            <Info size={18} />
                          </button>
                          </div>
                          <button
                            className={`absolute right-0 top-1/2 -translate-y-1/2 text-black dark:text-[color:var(--app-text-muted)] bg-[color:var(--app-surface)] rounded-full h-12 w-12 flex items-center justify-center transition-opacity duration-200 hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-ring)] ${
                              showSwipeInfo ? 'opacity-100' : 'opacity-0 pointer-events-none'
                            }`}
                            onClick={() => {
                              setSelectedListForDialog(selectedListId);
                              setOpenDialogId(task.id);
                            }}
                            data-task-info-button="true"
                          >
                            <Info size={18} />
                          </button>
                        </li>
                      );
                      }}
                    </Draggable>
                  );
                })}
                  <div className="mb-4">{provided.placeholder}</div>
                </ul>
              )}
            </Droppable>
          )}
     </DragDropContext>
        {renderSharedTaskDetailsModal()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      )}
      {/* Notice modal (replaces window.alert) */}
      <Modal
        isOpen={noticeOpen}
        onRequestClose={closeNotice}
        className="bg-[color:var(--app-surface)] rounded-2xl p-8 w-full max-w-xs mx-auto border-none shadow-2xl relative outline-none"
        overlayClassName="fixed inset-0 bg-[color:var(--app-overlay)] flex items-start justify-center z-[9999] pt-[calc(env(safe-area-inset-top)+0.5rem)] sm:pt-16"
        closeTimeoutMS={modalDurationMs}
        contentElement={(props, children) => (
          <motion.div
            {...props}
            initial={{ y: '100vh' }}
            animate={noticeOpen ? { y: 0 } : { y: '100vh' }}
            transition={modalTransition}
            style={{ willChange: 'transform' }}
          >
            {children}
          </motion.div>
        )}
      >
          <button
            className="absolute top-5 right-5 text-black dark:text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text-muted)] dark:hover:text-[color:var(--app-text)] text-xl"
            onClick={closeNotice}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
          <h3 className="text-[color:var(--app-text)] text-xl font-bold mb-6">{noticeTitle}</h3>
          <p className="text-[color:var(--app-text-secondary)] text-base">{noticeMessage}</p>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={closeNotice}
              className="bg-[color:var(--app-cta-bg)] text-[color:var(--app-cta-text)] rounded-2xl h-12 px-6 font-semibold shadow-sm transition-colors hover:shadow-md hover:bg-[color:var(--app-cta-hover)] active:bg-[color:var(--app-cta-active)]"
            >
              OK
            </button>
          </div>
      </Modal>
    </div>
  );
}

export default App;
